import { createContainer } from "unstated-next"
import * as voicifyApi from '../api';
import InvalidResult from "../models/result/InvalidResult";
import { useEffect, useState } from "react";
import moment from "moment";
import IResult from "../models/result/IResult";
import TopIntents from "../models/customAssistant/TopIntents";
import CallStatistics from "../models/customAssistant/CallStatistics";
import OrderTotalStatistics from "../models/analytics/OrderTotalStatisticsModel";
import TopLocationWithOrderCountCollection from "../models/analytics/LocationWithOrderCount";
import AnalyticsRequestFilter from "../models/analytics/AnalyticsRequestFilter";
import OrderPlacedEvent from "../models/analytics/OrderPlacedEvent";
import ConversationalSuccessEvent from "../models/analytics/ConversationalSuccessEvent";
import ItemAddedToOrderEvent from "../models/analytics/ItemAddedToOrderEvent";
import CallTransferEvents from "../models/analytics/CallTransferEvents";
import ImmediateCallTransferEvents from "../models/analytics/ImmediateCallTransferEvents";
import ApplicationsSearchResult from "../models/applications/api/ApplicationsSearchResult";
import OrderCompletionBreakDownPerAppCollection from "../models/analytics/OrderCompletionBreakDownPerAppCollection";
import CallTransferredRateModelCollection from "../models/analytics/CallTransferredRateModelCollection";
import ConversationalSuccessBreakdownCollection from "../models/analytics/ConversationalSuccessBreakdownCollection";
import CallImmediateTransferredRateModelCollection from "../models/analytics/CallImmediateTransferredRateModelCollection";
import AverageOrderTotalModelCollection from "../models/analytics/AverageOrderTotalModelCollection";
import SessionDateModel from "../models/analytics/api/SessionDateModel";
import HistogramModel from "../models/analytics/HistogramModel";
import { OrganizationEngagement, TopLevelMetrics, EngagementAnalyticsFilter, TransferReasonsApiResponse, LegacyTransferReason, GroupedHistogramData, GroupedTopLevelMetrics } from "../api/controllers/engagementAnalyticsReporting";
import { flattenGroupedHistogramData, flattenGroupedHistogramDataFiltered, flattenConcurrentCallHistogramDataFiltered, normalizeEngagementAnalyticsFilterToUtc, parseAnalyticsDate } from "../utils/engagementAnalyticsTransformers";

interface IOrgApplicationsPerformanceAnalytics {
    applicationId: string;
    OrderCompletionRate?: number;   // Optional since not all may have these
    ConversationalSuccessRate?: number;
    CallTransferredRate?: number;
    CallImmediateTransferredRate?: number;
    AverageOrderTotal?: number;
}
import { EXCLUDED_TOP_INTENTS } from "../constants/Intents";
import ListStat from "../models/customAssistant/ListStat";

function useOrgAnalyticsContainer(props) {
    const [error, _setError] = useState("");
    const setError = (err: any) => {
        if (err && (err.name === 'AbortError' || err.message === 'The user aborted a request.')) {
            return;
        }
        _setError(err?.message || err || "");
    };
    const [loadingTopIntents, setLoadingTopIntents] = useState(false);
    const [loadingOrdersPlaced, setLoadingOrdersPlaced] = useState(false);
    const [loadingCallStatistics, setLoadingCallStatistics] = useState(false);
    const [loadingConversationalSucess, setLoadingConversationalSuccess] = useState(false);
    const [loadingItemAddedToOrder, setLoadingItemAddedToOrder] = useState(false);
    const [loadingOrderTotalStatistics, setLoadingOrderTotalStatistics] = useState(false);
    const [loadingTopLocationWithHighestNumberOfOrders, setLoadingTopLocationWithHighestNumberOfOrders] = useState(false);
    const [loadingOrgAnalytics, setLoadingOrgAnalytics] = useState(false);
    const [loadingOrgApplicationsPerformanceAnalytics, setLoadOrgApplicationsPerformanceAnalytics] = useState(false);
    const [loadingCallTransferredEvents, setLoadingCallTransferredEvents] = useState(false);
    const [loadingImmediateCallTransferredEvents, setLoadingImmediateCallTransferredEvents] = useState(false);
    const [loadingSessionsOfAllApplications, setLoadingSessionsOfAllApplications] = useState(false);
    const [sessions, setSessions] = useState([] as SessionDateModel[]);
    const [allEventsCache, setAllEventsCache] = useState({});
    const [loadingSessionId, setLoadingSessionId] = useState("")
    const [callStatistics, setCallStatistics] = useState<CallStatistics>(null);
    const [orderPlacedEvents, setOrderPlacedEvents] = useState<OrderPlacedEvent[]>(null);
    const [topIntents, setTopIntents] = useState<ListStat[]>(null);
    const [conversationalSuccessEvents, setConversationalSuccessEvents] = useState<ConversationalSuccessEvent[]>(null);
    const [itemAddedConversationalSuccessEvents, setItemAddedConversationalSuccessEvents] = useState<ConversationalSuccessEvent[]>(null);
    const [itemAddedToOrderEvents, setItemAddedToOrderEvents] = useState<ItemAddedToOrderEvent[]>(null);
    const [callCompleteEvents, setCallCompleteEvents] = useState<any[]>(null);
    const [orderTotalStatistics, setOrderTotalStatistics] = useState<OrderTotalStatistics>(null);
    const [topLocationsWithNumberOfOrdersPlaced, setTopLocationsWithHighestNumberOfOrders] = useState<TopLocationWithOrderCountCollection>(null);
    const [callTransferredEvents, setCallTransferredEvents] = useState<CallTransferEvents[]>()
    const [immediateCallTransferredEvents, setImmediateCallTransferredEvents] = useState<ImmediateCallTransferEvents[]>()
    const [immediateTransfers, setImmediateTransfers] = useState(0);
    const [transfers, setTransfers] = useState(0);
    const [callTransferRate, setCallTransferRate] = useState<number>()
    const [immediateCallTransferRate, setImmediateCallTransferRate] = useState<number>()
    const [conversationalSuccessRate, setConversationalSuccessRate] = useState<number>()
    const [orderPlacedCount, setOrdersPlacedCount] = useState<number>(0);
    const [attemptedOrdersConversationalSuccessRate, setAttemptedOrdersConversationalSuccessRate] = useState<number>(null);
    const [orderPlacedRate, setOrderPlacedRate] = useState<number>(null);
    const [orderTransferRate, setOrderTransferRate] = useState<number>(null);
    const [orderTransfers, setOrderTransfers] = useState<number>(null);
    const [conversationalSuccessCount, setConversationalSuccessCount] = useState<number>(null);
    const [conversationalSuccessWithOrderAttemptedCount, setConversationalSuccessWithOrderAttemptedCount] = useState<number>(null);
    const [orderAttempts, setOrderAttempts] = useState<number>();
    const [orgApplicationsPerformanceAnalytics, setOrgApplicationsPerformanceAnalytics] = useState<IOrgApplicationsPerformanceAnalytics[]>();
    const [unsuccessfulOrderAttempts, setUnsuccessfulOrderAttempts] = useState<number>(0);
    const [noInteractionCount, setNoInteractionCount] = useState<number>(0);
    const [unsuccessfulButTransfered, setUnsuccessfulButTransfered] = useState<number>(0);
    const NUMBER_OF_TOP_LOCATIONS_WITH_HIGHEST_ORDERS = 3;
    const [unsuccessfulOrderSessions, setUnsuccessfulOrderSessions] = useState<SessionDateModel[]>([]);
    const [ordersPlacedSessions, setOrdersPlacedSessions] = useState([]);
    const [successfulOrdersNotPlacedSessions, setSuccessfulOrdersNotPlacedSessions] = useState<SessionDateModel[]>([]);
    const [orderTransferredSessionIds, setOrderTransferredSessionIds] = useState([]);
    const [noOrderSessionIds, setNoOrderSessionIds] = useState([]);
    const [noTransferUnsuccessfulSessions, setNoTransferUnsuccessfulSessions] = useState<SessionDateModel[]>([]);
    const [transferUnsuccessfulSessions, setTransferUnsuccessfulSessions] = useState<SessionDateModel[]>([]);
    const [conversationalSuccessSessions, setConversationalSuccessSessions] = useState<SessionDateModel[]>([]);
    const [sessionIdCount, setSessionIdCount] = useState(0);
    const [reloadBool, setReloadBool] = useState(false);

    // Engagement Analytics State
    // Initialize to true so loader shows on first render until data loads
    const [loadingOrganizationEngagements, setLoadingOrganizationEngagements] = useState(true);
    const [loadingPlatformEngagements, setLoadingPlatformEngagements] = useState(false);
    const [loadingEngagementsForTagCombo, setLoadingEngagementsForTagCombo] = useState(false);
    const [loadingPlatformEngagementsForTagCombo, setLoadingPlatformEngagementsForTagCombo] = useState(false);
    const [loadingTopLevelMetrics, setLoadingTopLevelMetrics] = useState(false);
    const [loadingOrganizationTopLevelMetrics, setLoadingOrganizationTopLevelMetrics] = useState(false);
    const [loadingPlatformTopLevelMetrics, setLoadingPlatformTopLevelMetrics] = useState(false);
    const [loadingCallVolumeHistogram, setLoadingCallVolumeHistogram] = useState(false);
    const [loadingOrganizationCallVolumeHistogram, setLoadingOrganizationCallVolumeHistogram] = useState(false);
    const [loadingPlatformCallVolumeHistogram, setLoadingPlatformCallVolumeHistogram] = useState(false);
    const [loadingAverageCallVolumeHistogram, setLoadingAverageCallVolumeHistogram] = useState(false);
    const [loadingOrganizationAverageCallVolumeHistogram, setLoadingOrganizationAverageCallVolumeHistogram] = useState(false);
    const [loadingOrganizationAverageCallVolumeByHourHistogram, setLoadingOrganizationAverageCallVolumeByHourHistogram] = useState(false);
    const [loadingPlatformAverageCallVolumeHistogram, setLoadingPlatformAverageCallVolumeHistogram] = useState(false);
    const [loadingPlatformAverageCallVolumeByHourHistogram, setLoadingPlatformAverageCallVolumeByHourHistogram] = useState(false);
    const [loadingContainableCallVolumeHistogram, setLoadingContainableCallVolumeHistogram] = useState(false);
    const [loadingOrganizationContainableCallVolumeHistogram, setLoadingOrganizationContainableCallVolumeHistogram] = useState(false);
    const [loadingPlatformContainableCallVolumeHistogram, setLoadingPlatformContainableCallVolumeHistogram] = useState(false);
    const [loadingContainedCallVolumeHistogram, setLoadingContainedCallVolumeHistogram] = useState(false);
    const [loadingOrganizationContainedCallVolumeHistogram, setLoadingOrganizationContainedCallVolumeHistogram] = useState(false);
    const [loadingPlatformContainedCallVolumeHistogram, setLoadingPlatformContainedCallVolumeHistogram] = useState(false);
    const [loadingAverageContainableCallVolumeHistogram, setLoadingAverageContainableCallVolumeHistogram] = useState(false);
    const [loadingOrganizationAverageContainableCallVolumeHistogram, setLoadingOrganizationAverageContainableCallVolumeHistogram] = useState(false);
    const [loadingAverageContainedCallVolumeHistogram, setLoadingAverageContainedCallVolumeHistogram] = useState(false);
    const [loadingOrganizationAverageContainedCallVolumeHistogram, setLoadingOrganizationAverageContainedCallVolumeHistogram] = useState(false);
    const [loadingSpanishCallVolumeHistogram, setLoadingSpanishCallVolumeHistogram] = useState(false);
    const [loadingOrganizationSpanishCallVolumeHistogram, setLoadingOrganizationSpanishCallVolumeHistogram] = useState(false);
    const [loadingPlatformSpanishCallVolumeHistogram, setLoadingPlatformSpanishCallVolumeHistogram] = useState(false);
    const [loadingSpanishContainableCallVolumeHistogram, setLoadingSpanishContainableCallVolumeHistogram] = useState(false);
    const [loadingOrganizationSpanishContainableCallVolumeHistogram, setLoadingOrganizationSpanishContainableCallVolumeHistogram] = useState(false);
    const [loadingPlatformSpanishContainableCallVolumeHistogram, setLoadingPlatformSpanishContainableCallVolumeHistogram] = useState(false);
    const [loadingSpanishContainedCallVolumeHistogram, setLoadingSpanishContainedCallVolumeHistogram] = useState(false);
    const [loadingOrganizationSpanishContainedCallVolumeHistogram, setLoadingOrganizationSpanishContainedCallVolumeHistogram] = useState(false);
    const [loadingPlatformSpanishContainedCallVolumeHistogram, setLoadingPlatformSpanishContainedCallVolumeHistogram] = useState(false);
    const [loadingConcurrentCallVolumeHistogram, setLoadingConcurrentCallVolumeHistogram] = useState(false);
    const [loadingOrganizationConcurrentCallVolumeHistogram, setLoadingOrganizationConcurrentCallVolumeHistogram] = useState(false);
    const [loadingPlatformConcurrentCallVolumeHistogram, setLoadingPlatformConcurrentCallVolumeHistogram] = useState(false);
    const [organizationEngagements, setOrganizationEngagements] = useState<OrganizationEngagement[]>([]);
    const [organizationEngagementsPerApp, setOrganizationEngagementsPerApp] = useState<Record<string, OrganizationEngagement[]>>({});
    const [platformEngagements, setPlatformEngagements] = useState<OrganizationEngagement[]>([]);
    const [platformEngagementsPerOrg, setPlatformEngagementsPerOrg] = useState<Record<string, OrganizationEngagement[]>>({});
    const [engagementsForTagCombo, setEngagementsForTagCombo] = useState<OrganizationEngagement[]>([]);
    const [platformEngagementsForTagCombo, setPlatformEngagementsForTagCombo] = useState<OrganizationEngagement[]>([]);
    const [topLevelMetrics, setTopLevelMetrics] = useState<TopLevelMetrics>(null);
    const [organizationTopLevelMetrics, setOrganizationTopLevelMetrics] = useState<TopLevelMetrics>(null);
    const [organizationTopLevelMetricsPerApp, setOrganizationTopLevelMetricsPerApp] = useState<Record<string, TopLevelMetrics>>({});
    const [platformTopLevelMetrics, setPlatformTopLevelMetrics] = useState<TopLevelMetrics>(null);
    const [platformTopLevelMetricsPerOrg, setPlatformTopLevelMetricsPerOrg] = useState<Record<string, TopLevelMetrics>>({});
    const [loadingPlatformUpsoldRevenue, setLoadingPlatformUpsoldRevenue] = useState(false);
    const [platformUpsoldRevenuePerOrg, setPlatformUpsoldRevenuePerOrg] = useState<Record<string, number>>({});
    const [callVolumeHistogram, setCallVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationCallVolumeHistogram, setOrganizationCallVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationCallVolumeHistogramPerApp, setOrganizationCallVolumeHistogramPerApp] = useState<GroupedHistogramData>({});
    const [platformCallVolumeHistogram, setPlatformCallVolumeHistogram] = useState<HistogramModel>(null);
    const [platformCallVolumeHistogramPerOrg, setPlatformCallVolumeHistogramPerOrg] = useState<GroupedHistogramData>({});
    const [averageCallVolumeHistogram, setAverageCallVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationAverageCallVolumeHistogram, setOrganizationAverageCallVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationAverageCallVolumeHistogramPerApp, setOrganizationAverageCallVolumeHistogramPerApp] = useState<GroupedHistogramData>({});
    const [organizationAverageCallVolumeByHourHistogram, setOrganizationAverageCallVolumeByHourHistogram] = useState<HistogramModel>(null);
    const [organizationAverageCallVolumeByHourHistogramPerApp, setOrganizationAverageCallVolumeByHourHistogramPerApp] = useState<GroupedHistogramData>({});
    const [platformAverageCallVolumeHistogram, setPlatformAverageCallVolumeHistogram] = useState<HistogramModel>(null);
    const [platformAverageCallVolumeHistogramPerOrg, setPlatformAverageCallVolumeHistogramPerOrg] = useState<GroupedHistogramData>({});
    const [platformAverageCallVolumeByHourHistogram, setPlatformAverageCallVolumeByHourHistogram] = useState<HistogramModel>(null);
    const [platformAverageCallVolumeByHourHistogramPerOrg, setPlatformAverageCallVolumeByHourHistogramPerOrg] = useState<GroupedHistogramData>({});
    const [containableCallVolumeHistogram, setContainableCallVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationContainableCallVolumeHistogram, setOrganizationContainableCallVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationContainableCallVolumeHistogramPerApp, setOrganizationContainableCallVolumeHistogramPerApp] = useState<GroupedHistogramData>({});
    const [platformContainableCallVolumeHistogram, setPlatformContainableCallVolumeHistogram] = useState<HistogramModel>(null);
    const [platformContainableCallVolumeHistogramPerOrg, setPlatformContainableCallVolumeHistogramPerOrg] = useState<GroupedHistogramData>({});
    const [containedCallVolumeHistogram, setContainedCallVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationContainedCallVolumeHistogram, setOrganizationContainedCallVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationContainedCallVolumeHistogramPerApp, setOrganizationContainedCallVolumeHistogramPerApp] = useState<GroupedHistogramData>({});
    const [platformContainedCallVolumeHistogram, setPlatformContainedCallVolumeHistogram] = useState<HistogramModel>(null);
    const [platformContainedCallVolumeHistogramPerOrg, setPlatformContainedCallVolumeHistogramPerOrg] = useState<GroupedHistogramData>({});
    const [averageContainableCallVolumeHistogram, setAverageContainableCallVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationAverageContainableCallVolumeHistogram, setOrganizationAverageContainableCallVolumeHistogram] = useState<HistogramModel>(null);
    const [averageContainedCallVolumeHistogram, setAverageContainedCallVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationAverageContainedCallVolumeHistogram, setOrganizationAverageContainedCallVolumeHistogram] = useState<HistogramModel>(null);
    const [spanishCallVolumeHistogram, setSpanishCallVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationSpanishCallVolumeHistogram, setOrganizationSpanishCallVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationSpanishCallVolumeHistogramPerApp, setOrganizationSpanishCallVolumeHistogramPerApp] = useState<GroupedHistogramData>({});
    const [platformSpanishCallVolumeHistogram, setPlatformSpanishCallVolumeHistogram] = useState<HistogramModel>(null);
    const [platformSpanishCallVolumeHistogramPerOrg, setPlatformSpanishCallVolumeHistogramPerOrg] = useState<GroupedHistogramData>({});
    const [spanishContainableCallVolumeHistogram, setSpanishContainableCallVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationSpanishContainableCallVolumeHistogram, setOrganizationSpanishContainableCallVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationSpanishContainableCallVolumeHistogramPerApp, setOrganizationSpanishContainableCallVolumeHistogramPerApp] = useState<GroupedHistogramData>({});
    const [platformSpanishContainableCallVolumeHistogram, setPlatformSpanishContainableCallVolumeHistogram] = useState<HistogramModel>(null);
    const [platformSpanishContainableCallVolumeHistogramPerOrg, setPlatformSpanishContainableCallVolumeHistogramPerOrg] = useState<GroupedHistogramData>({});
    const [spanishContainedCallVolumeHistogram, setSpanishContainedCallVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationSpanishContainedCallVolumeHistogram, setOrganizationSpanishContainedCallVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationSpanishContainedCallVolumeHistogramPerApp, setOrganizationSpanishContainedCallVolumeHistogramPerApp] = useState<GroupedHistogramData>({});
    const [platformSpanishContainedCallVolumeHistogram, setPlatformSpanishContainedCallVolumeHistogram] = useState<HistogramModel>(null);
    const [platformSpanishContainedCallVolumeHistogramPerOrg, setPlatformSpanishContainedCallVolumeHistogramPerOrg] = useState<GroupedHistogramData>({});
    const [concurrentCallVolumeHistogram, setConcurrentCallVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationConcurrentCallVolumeHistogram, setOrganizationConcurrentCallVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationConcurrentCallVolumeHistogramPerApp, setOrganizationConcurrentCallVolumeHistogramPerApp] = useState<GroupedHistogramData>({});
    const [platformConcurrentCallVolumeHistogram, setPlatformConcurrentCallVolumeHistogram] = useState<HistogramModel>(null);
    const [platformConcurrentCallVolumeHistogramPerOrg, setPlatformConcurrentCallVolumeHistogramPerOrg] = useState<GroupedHistogramData>({});

    // Order Volume State
    const [loadingOrderAttemptedVolumeHistogram, setLoadingOrderAttemptedVolumeHistogram] = useState(false);
    const [loadingOrganizationOrderAttemptedVolumeHistogram, setLoadingOrganizationOrderAttemptedVolumeHistogram] = useState(false);
    const [loadingPlatformOrderAttemptedVolumeHistogram, setLoadingPlatformOrderAttemptedVolumeHistogram] = useState(false);
    const [loadingOrderPlacedVolumeHistogram, setLoadingOrderPlacedVolumeHistogram] = useState(false);
    const [loadingOrganizationOrderPlacedVolumeHistogram, setLoadingOrganizationOrderPlacedVolumeHistogram] = useState(false);
    const [loadingPlatformOrderPlacedVolumeHistogram, setLoadingPlatformOrderPlacedVolumeHistogram] = useState(false);
    const [orderAttemptedVolumeHistogram, setOrderAttemptedVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationOrderAttemptedVolumeHistogram, setOrganizationOrderAttemptedVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationOrderAttemptedVolumeHistogramPerApp, setOrganizationOrderAttemptedVolumeHistogramPerApp] = useState<GroupedHistogramData>({});
    const [platformOrderAttemptedVolumeHistogram, setPlatformOrderAttemptedVolumeHistogram] = useState<HistogramModel>(null);
    const [platformOrderAttemptedVolumeHistogramPerOrg, setPlatformOrderAttemptedVolumeHistogramPerOrg] = useState<GroupedHistogramData>({});
    const [orderPlacedVolumeHistogram, setOrderPlacedVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationOrderPlacedVolumeHistogram, setOrganizationOrderPlacedVolumeHistogram] = useState<HistogramModel>(null);
    const [organizationOrderPlacedVolumeHistogramPerApp, setOrganizationOrderPlacedVolumeHistogramPerApp] = useState<GroupedHistogramData>({});
    const [platformOrderPlacedVolumeHistogram, setPlatformOrderPlacedVolumeHistogram] = useState<HistogramModel>(null);
    const [platformOrderPlacedVolumeHistogramPerOrg, setPlatformOrderPlacedVolumeHistogramPerOrg] = useState<GroupedHistogramData>({});

    // Transfer Reasons State
    const [loadingOrganizationTransferReasons, setLoadingOrganizationTransferReasons] = useState(false);
    const [loadingPlatformTransferReasons, setLoadingPlatformTransferReasons] = useState(false);
    const [loadingApplicationTransferReasons, setLoadingApplicationTransferReasons] = useState(false);
    const [organizationTransferReasons, setOrganizationTransferReasons] = useState<TransferReasonsApiResponse | null>(null);
    const [organizationPlannedTransferReasons, setOrganizationPlannedTransferReasons] = useState<TransferReasonsApiResponse | null>(null);
    const [organizationUnplannedTransferReasons, setOrganizationUnplannedTransferReasons] = useState<TransferReasonsApiResponse | null>(null);
    const [platformTransferReasons, setPlatformTransferReasons] = useState<TransferReasonsApiResponse | null>(null);
    const [platformPlannedTransferReasons, setPlatformPlannedTransferReasons] = useState<TransferReasonsApiResponse | null>(null);
    const [platformUnplannedTransferReasons, setPlatformUnplannedTransferReasons] = useState<TransferReasonsApiResponse | null>(null);
    const [applicationTransferReasons, setApplicationTransferReasons] = useState<Record<string, number> | null>(null);

    // Appointments Managed State (for Patient Bookings chart)
    const [loadingScheduledEngagements, setLoadingScheduledEngagements] = useState(false);
    const [loadingRescheduledEngagements, setLoadingRescheduledEngagements] = useState(false);
    const [loadingCancelledEngagements, setLoadingCancelledEngagements] = useState(false);
    const [loadingCheckNextAppointmentEngagements, setLoadingCheckNextAppointmentEngagements] = useState(false);
    const [scheduledEngagements, setScheduledEngagements] = useState<OrganizationEngagement[]>([]);
    const [rescheduledEngagements, setRescheduledEngagements] = useState<OrganizationEngagement[]>([]);
    const [cancelledEngagements, setCancelledEngagements] = useState<OrganizationEngagement[]>([]);
    const [checkNextAppointmentEngagements, setCheckNextAppointmentEngagements] = useState<OrganizationEngagement[]>([]);

    // Off Hours Engagements State
    const [loadingOffHoursEngagements, setLoadingOffHoursEngagements] = useState(false);
    const [offHoursEngagements, setOffHoursEngagements] = useState<OrganizationEngagement[]>([]);

    // Spanish Off Hours Engagements State (for language breakdown)
    const [loadingSpanishOffHoursEngagements, setLoadingSpanishOffHoursEngagements] = useState(false);
    const [spanishOffHoursEngagements, setSpanishOffHoursEngagements] = useState<OrganizationEngagement[]>([]);

    // Transferred Engagements State (for Spanish-level transfer data)
    const [loadingTransferredEngagements, setLoadingTransferredEngagements] = useState(false);
    const [transferredEngagements, setTransferredEngagements] = useState<OrganizationEngagement[]>([]);

    const normalizeFilterOrThrow = (
        filter: EngagementAnalyticsFilter,
        options?: { timezone?: string | undefined }
    ): EngagementAnalyticsFilter => normalizeEngagementAnalyticsFilterToUtc(filter, options);

    const normalizeHistogramFilterOrThrow = (
        filter: EngagementAnalyticsFilter,
        options?: { timezone?: string | undefined }
    ): EngagementAnalyticsFilter => {
        const isDateOnly = (dateStr: string): boolean => moment(dateStr, 'YYYY-MM-DD', true).isValid();
        const hasDateTimePrecision = !isDateOnly(filter.startDate) || !isDateOnly(filter.endDate);

        const startMoment = parseAnalyticsDate(filter.startDate);
        const endMoment = parseAnalyticsDate(filter.endDate);

        if (!startMoment.isValid() || !endMoment.isValid()) {
            throw new Error(`Invalid analytics histogram date filter. startDate=${filter.startDate}, endDate=${filter.endDate}`);
        }

        const normalizedStart = isDateOnly(filter.startDate) ? startMoment.clone().startOf('day') : startMoment.clone();
        const normalizedEnd = isDateOnly(filter.endDate) ? endMoment.clone().endOf('day') : endMoment.clone();

        return {
            ...filter,
            startDate: normalizedStart.format('YYYY-MM-DDTHH:mm:ss.SSS'),
            endDate: normalizedEnd.format('YYYY-MM-DDTHH:mm:ss.SSS'),
            timezone: options?.timezone ?? filter.timezone
        };
    };

    useEffect(() => {
        if (!itemAddedToOrderEvents || !orderPlacedEvents || !conversationalSuccessEvents || !callStatistics || !callCompleteEvents?.length || !sessions.length) return

        const allSessionIds = getUniqueSessionIds(sessions);
        const itemAddedToOrderSessionIds = getUniqueSessionIds(itemAddedToOrderEvents)
        const callTransferedSessionIds = getUniqueSessionIds(callTransferredEvents);

        const orderAttempts = itemAddedToOrderSessionIds.length;
        setOrderAttempts(orderAttempts)
        const placedSessionIds = getUniqueSessionIds(orderPlacedEvents).filter(id => itemAddedToOrderSessionIds.includes(id));
        setOrdersPlacedSessions(matchSessionWithIdsAndSort(placedSessionIds));
        setOrdersPlacedCount(getUniqueSessionIds(orderPlacedEvents)?.length ?? 0);

        const conversationalSuccessEventsOverride = conversationalSuccessEvents.filter(x => allSessionIds.includes(x.sessionId));
        setConversationalSuccessEvents(conversationalSuccessEventsOverride);
        const conversationalSuccessEventSessions = getUniqueSessionIds(conversationalSuccessEventsOverride);
        setSessionIdCount(allSessionIds.length);
        const preliminaryUnsuccessList = [];
        const shownConversationalSuccessEvents = [];
        allSessionIds.forEach(sessionId => {
            if (conversationalSuccessEventSessions.includes(sessionId)) {
                shownConversationalSuccessEvents.push(sessionId);
            } else {
                preliminaryUnsuccessList.push(sessionId);
            }
        });
        setConversationalSuccessSessions(matchSessionWithIdsAndSort(shownConversationalSuccessEvents));
        setConversationalSuccessCount(shownConversationalSuccessEvents.length);
        const finalUnsuccessList = [];
        const tUnsucessTransferIds = [];
        const usuccessOrderAttemptsIds = []
        if (preliminaryUnsuccessList.length > 0) {
            let unsuccessfulInteractions = preliminaryUnsuccessList.filter(sessionId => {
                const callComplete = callCompleteEvents.find(x => x.sessionId === sessionId);
                if (callComplete) {
                    if (callComplete.newTTSGenerations + callComplete.cachedTTSGenerations < 2) {
                        return false;
                    }
                }
                return true;
            });
            setNoInteractionCount(preliminaryUnsuccessList.length - unsuccessfulInteractions.length);

            unsuccessfulInteractions.forEach(sessionId => {
                if (callTransferedSessionIds.includes(sessionId)) {
                    tUnsucessTransferIds.push(sessionId);
                } else {
                    finalUnsuccessList.push(sessionId);
                }
                if (itemAddedToOrderSessionIds.includes(sessionId)) {
                    usuccessOrderAttemptsIds.push(sessionId);
                }
            });
            setUnsuccessfulOrderAttempts(usuccessOrderAttemptsIds.length);
        }
        setNoTransferUnsuccessfulSessions(matchSessionWithIdsAndSort(finalUnsuccessList));

        setTransferUnsuccessfulSessions(matchSessionWithIdsAndSort(tUnsucessTransferIds));
        setUnsuccessfulButTransfered(tUnsucessTransferIds.length);

        setCallTransferRate(callTransferedSessionIds.length / callStatistics.calls);
        setTransfers(callTransferedSessionIds.length)
        // union of call transferred and order attempted session ids
        const oTransferSessIds = callTransferedSessionIds.filter(x => itemAddedToOrderSessionIds.includes(x));
        setOrderTransferredSessionIds(oTransferSessIds);

        const immediateCallTransferedSessionIds = getUniqueSessionIds(immediateCallTransferredEvents);
        setImmediateCallTransferRate(immediateCallTransferedSessionIds.length / callStatistics.calls);
        setImmediateTransfers(immediateCallTransferedSessionIds.length)

        const successfulOrderNotPlacedSessionIds = itemAddedToOrderSessionIds.filter(x => !placedSessionIds.includes(x) && conversationalSuccessEventSessions.includes(x));
        setSuccessfulOrdersNotPlacedSessions(matchSessionWithIdsAndSort(successfulOrderNotPlacedSessionIds));
        const noOrderSessions = sessions.filter(x => !itemAddedToOrderSessionIds.includes(x.sessionId));
        setNoOrderSessionIds(noOrderSessions.map(s => s.sessionId));
        if (!itemAddedToOrderSessionIds?.length) {
            setOrderPlacedRate(-1);
            setOrderTransferRate(-1);
            setOrderTransfers(-1);
        } else {
            setOrderPlacedRate(placedSessionIds.length / itemAddedToOrderSessionIds.length);
            const orderTransferedSessionIds = callTransferedSessionIds.filter(x => itemAddedToOrderSessionIds.includes(x));
            setOrderTransfers(orderTransferedSessionIds.length);
            setOrderTransferRate(orderTransferedSessionIds.length / itemAddedToOrderSessionIds.length);

            const allConversationalSuccessRate = shownConversationalSuccessEvents.length / (callStatistics.calls ?? 1);
            setConversationalSuccessRate(allConversationalSuccessRate)


            if (itemAddedToOrderSessionIds?.length > 0) {
                const orderAttemptedConversationalSuccess = itemAddedToOrderSessionIds.filter(id => placedSessionIds.includes(id) || shownConversationalSuccessEvents.includes(id));
                setConversationalSuccessWithOrderAttemptedCount(orderAttemptedConversationalSuccess.length);
                const itemAddedConversationalSuccessRate = orderAttemptedConversationalSuccess.length / orderAttempts;
                setAttemptedOrdersConversationalSuccessRate(itemAddedConversationalSuccessRate);

                const temp_unseccessfulOrderSessionIds = itemAddedToOrderSessionIds.filter(x => !placedSessionIds.includes(x) && !shownConversationalSuccessEvents.includes(x));
                setUnsuccessfulOrderSessions(matchSessionWithIdsAndSort(temp_unseccessfulOrderSessionIds));
            }
        }

    }, [itemAddedToOrderEvents, orderPlacedEvents, callStatistics, callCompleteEvents, sessions, reloadBool, callTransferredEvents])

    const matchSessionWithIdsAndSort = (ids: string[]) => {
        var tempArr = [];
        for (const sessionId of ids) {
            const session = sessions?.find(s => s.sessionId === sessionId);
            if (session) {
                tempArr.push(session);
            }
        }
        tempArr.sort((a, b) => {
            return new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
        })
        return tempArr;
    }

    const getOrgLevelCallStatistics = async (organizationId: string, filter: AnalyticsRequestFilter): Promise<IResult<CallStatistics>> => {
        setLoadingCallStatistics(true);
        const result = await voicifyApi.getCallStatistics(organizationId, filter);
        setLoadingCallStatistics(false);
        if (result.resultType === "Ok") {
            setCallStatistics(result.data);
        }
        else {
            setError(result.errors?.[0]);
            return new InvalidResult<CallStatistics>(result.errors?.[0]);
        }
    }

    const getOrgItemAddedConversationalSuccessEvents = async (organizationId: string, filter: AnalyticsRequestFilter): Promise<IResult<ConversationalSuccessEvent>> => {
        const result = await voicifyApi.getItemAddedConversationalSuccessEvents(organizationId, filter);
        if (result.resultType === "Ok") {
            setItemAddedConversationalSuccessEvents(result.data);
        }
        else {
            setError(result.errors?.[0]);
            return new InvalidResult<ConversationalSuccessEvent>(result.errors?.[0]);
        }
    }

    const getOrgLevelTopIntents = async (organizationId: string, filter: AnalyticsRequestFilter): Promise<void> => {
        setLoadingTopIntents(true);
        const result = await voicifyApi.getTopIntents(organizationId, filter);
        setLoadingTopIntents(false);
        if (result.resultType === "Ok") {
            const intents: TopIntents[] = [];
            for (const intent of result.data) {
                if (!intent.intent?.trim() || EXCLUDED_TOP_INTENTS.includes(intent.intent)) {
                    continue;
                } else {
                    intents.push(intent);
                }
            }
            const sortedIntents = intents.sort((a, b) => b.count - a.count);
            const allIntents = [];
            for (const intent of sortedIntents) {
                // Add spaces in between capital letters and before punctuation
                const splitIntent = intent.intent.split(/(?=[A-Z.,:;!?-])/).join(" ");
                allIntents.push({ name: splitIntent, count: intent.count.toLocaleString() })
            }
            setTopIntents(allIntents);
        }
        else {
            setError(result.errors?.[0]);
        }
    }

    const getOrgLevelOrdersPlaced = async (organizationId: string, filter: AnalyticsRequestFilter): Promise<void> => {
        setLoadingOrdersPlaced(true);
        const result = await voicifyApi.getOrderPlacedEvents(organizationId, filter);
        setLoadingOrdersPlaced(false);
        if (result.resultType === "Ok") {
            setOrderPlacedEvents(result.data)
        }
        else {
            setError(result.errors?.[0]);
        }
    }

    const getOrgLevelConversationalSuccess = async (organizationId: string, filter: AnalyticsRequestFilter): Promise<void> => {
        setLoadingConversationalSuccess(true);
        const result = await voicifyApi.getConversationalSuccessEvents(organizationId, filter);
        setLoadingConversationalSuccess(false);
        if (result.resultType === "Ok") {
            setConversationalSuccessEvents(result.data)
            setReloadBool(true);
        }
        else {
            setError(result.errors?.[0]);
        }
    }

    const getOrgLevelItemAddedToOrder = async (organizationId: string, filter: AnalyticsRequestFilter): Promise<void> => {
        setLoadingItemAddedToOrder(true);
        const result = await voicifyApi.getItemAddedToOrderEvents(organizationId, filter);
        setLoadingItemAddedToOrder(false);
        if (result.resultType === "Ok") {
            setItemAddedToOrderEvents(result.data)
        }
        else {
            setError(result.errors?.[0]);
        }
    }

    const getOrgLevelCallCompleteEvents = async (organizationId: string, filter: AnalyticsRequestFilter): Promise<void> => {
        const result = await voicifyApi.getOrgCallCompleteEvents(organizationId, filter);
        if (result.resultType === "Ok") {
            setCallCompleteEvents(result.data)
        }
        else {
            setError(result.errors?.[0]);
        }
    }



    const getOrgLevelOrderTotalStatistics = async (organizationId: string, filter: AnalyticsRequestFilter): Promise<void> => {
        setLoadingOrderTotalStatistics(true);
        const result = await voicifyApi.getOrderTotalStatistics(organizationId, filter);
        setLoadingOrderTotalStatistics(false);
        if (result.resultType === "Ok") {
            setOrderTotalStatistics(result.data)
        }
        else {
            setError(result.errors?.[0]);
        }
    }

    const getTopLocationsWithNumberOfOrdersPlaced = async (organizationId: string, filter: AnalyticsRequestFilter): Promise<void> => {
        setLoadingTopLocationWithHighestNumberOfOrders(true);
        const result = await voicifyApi.getTopLocationsWithNumberOfOrdersPlaced(organizationId, filter);
        setLoadingTopLocationWithHighestNumberOfOrders(false);
        if (result.resultType === "Ok") {
            setTopLocationsWithHighestNumberOfOrders(result.data)
        }
        else {
            setError(result.errors?.[0]);
        }
    }

    const getUniqueSessionIds = (events: any[]) => {
        if (!events) { return [] }
        events = events.filter(x => x?.sessionId?.length);
        const grouped = events.reduce((acc, item) => {
            if (!acc[item.sessionId]) {
                acc[item.sessionId] = [];
            }
            acc[item.sessionId].push(item);
            return acc;
        }, {});
        return Object.keys(grouped);
    };

    // Maximum number of sessions to cache to prevent memory issues
    const MAX_EVENTS_CACHE_SIZE = 50;

    const loadAllEventsForSession = async (sessionId: string, applicationId: string) => {
        if (sessionId in allEventsCache) {
            return allEventsCache[sessionId];
        } else {
            setLoadingSessionId(sessionId);
            const allEvents = await voicifyApi.getAllEventsForSession(applicationId, sessionId);
            if (allEvents.resultType === "Ok") {
                allEvents.data.forEach(e => {
                    if (e.eventDate) {
                        e.timestamp = new Date(e.eventDate).getTime()
                    }
                })
                // sort by timestamp
                allEvents.data.sort((a, b) => a.timestamp - b.timestamp);
                const newEventsCache = { ...allEventsCache };

                // Limit cache size to prevent memory issues - remove oldest entries if at limit
                const cacheKeys = Object.keys(newEventsCache);
                if (cacheKeys.length >= MAX_EVENTS_CACHE_SIZE) {
                    // Remove the first (oldest) entry
                    delete newEventsCache[cacheKeys[0]];
                }

                newEventsCache[sessionId] = allEvents.data;
                setAllEventsCache(newEventsCache);
            }
            setLoadingSessionId("");
            return allEvents;
        }
    };

    // Clear the events cache to free memory
    const clearEventsCache = () => {
        setAllEventsCache({});
    };

    const getTransferredEvents = async (organizationId: string, filter: AnalyticsRequestFilter) => {
        setLoadingCallTransferredEvents(true);
        const result = await voicifyApi.getCallTransferredEvents(organizationId, filter);
        setLoadingCallTransferredEvents(false);
        if (result.resultType === "Ok") {
            setCallTransferredEvents(result.data)
        }
        else {
            setError(result.errors?.[0]);
        }
    };
    const getImmediateCallTransferredEvents = async (organizationId: string, filter: AnalyticsRequestFilter) => {
        setLoadingImmediateCallTransferredEvents(true);
        const result = await voicifyApi.getImmedirateCallTransferredEvents(organizationId, filter);
        setLoadingImmediateCallTransferredEvents(false);
        if (result.resultType === "Ok") {
            setImmediateCallTransferredEvents(result.data)
        }
        else {
            setError(result.errors?.[0]);
        }
    };

    const getSessionsOfAllApplications = async (organizationId: string, filter: AnalyticsRequestFilter) => {
        setLoadingSessionsOfAllApplications(true)
        setSessions([])
        let skip = -1;
        let take = -1;
        let result = await voicifyApi.getSessionsByOrganizationId(organizationId, filter, skip, take)
        let tempSessions: SessionDateModel[] = []
        tempSessions = [...tempSessions, ...result.data]
        // while (result.resultType === "Ok" && result.data.length === take) {
        //     skip += take;
        //     result = await voicifyApi.getSessionsByOrganizationId(organizationId, filter, skip, take)
        //     tempSessions = [...tempSessions, ...result.data]
        // }
        if (result.resultType !== "Ok") {
            setError(result.errors[0]);
            setLoadingSessionsOfAllApplications(false)
            return;
        }
        setSessions(tempSessions)
        setLoadingSessionsOfAllApplications(false)
    }


    const loadOrgAnalytics = async (organizationId: string, filter: AnalyticsRequestFilter) => {
        setLoadingOrgAnalytics(true)
        setCallStatistics(null)
        setTopIntents(null)
        setOrdersPlacedCount(0)
        setConversationalSuccessRate(null)
        setOrderTotalStatistics(null)
        setTopLocationsWithHighestNumberOfOrders(null)
        setCallTransferredEvents(null)
        setImmediateCallTransferredEvents(null)
        setItemAddedConversationalSuccessEvents(null);
        setSessionIdCount(0);
        try {
            const orgAnalyticsTasks: Array<Promise<unknown>> = [
                getSessionsOfAllApplications(organizationId, filter),
                getOrgLevelCallStatistics(organizationId, filter),
                getOrgLevelTopIntents(organizationId, filter),
                getOrgLevelOrdersPlaced(organizationId, filter),
                getOrgLevelItemAddedToOrder(organizationId, filter),
                getOrgLevelCallCompleteEvents(organizationId, filter),
                getOrgLevelConversationalSuccess(organizationId, filter),
                getOrgLevelOrderTotalStatistics(organizationId, filter),
                getTopLocationsWithNumberOfOrdersPlaced(organizationId, { ...filter, count: NUMBER_OF_TOP_LOCATIONS_WITH_HIGHEST_ORDERS }),
                getTransferredEvents(organizationId, filter),
                getImmediateCallTransferredEvents(organizationId, filter),
                getOrgItemAddedConversationalSuccessEvents(organizationId, filter)
            ];

            await Promise.all(orgAnalyticsTasks);
        }
        catch (error) {
            setError(error)
        } finally {
            setLoadingOrgAnalytics(false)
        }

    };


    const loadOrgApplicationsPerformanceAnalytics = async (organizationId: string, filter: AnalyticsRequestFilter) => {
        setLoadOrgApplicationsPerformanceAnalytics(true)
        const tasks = []
        tasks.push(voicifyApi.getOrderCompletionBreakDownPerApp(organizationId, filter))
        tasks.push(voicifyApi.getConversationalSuccessBreakdownPerApp(organizationId, filter))
        tasks.push(voicifyApi.getCallTransferredBreakdownPerApp(organizationId, filter))
        tasks.push(voicifyApi.getCallImmediateTransferredBreakdownPerApp(organizationId, filter))
        tasks.push(voicifyApi.getAverageOrderTotalBreakdownPerApp(organizationId, filter))
        tasks.push(voicifyApi.searchForApplications(organizationId, 0, 50, "NameAscending", ""))

        const results = await Promise.all(tasks);
        const errorResults = results.filter(x => x.resultType !== "Ok");
        if (errorResults.length) {
            setError(errorResults[0]);
            return;
        }

        const orderCompletion: OrderCompletionBreakDownPerAppCollection = results[0].data;
        const conversationalSuccess: ConversationalSuccessBreakdownCollection = results[1].data;
        const callTransferred: CallTransferredRateModelCollection = results[2].data;
        const callImmediateTransferred: CallImmediateTransferredRateModelCollection = results[3].data;
        const averageOrderTotal: AverageOrderTotalModelCollection = results[4].data;
        const availableApplications: ApplicationsSearchResult = results[5].data;

        const dataMap = new Map();
        // Function to update or add data by applicationId
        const addDataToMap = (array, key, fieldName) => {
            availableApplications.applications.forEach(app => {
                const appId = app.id; // Use app.id from allApps as the key
                if (!dataMap.has(appId)) {
                    // If the app is not found in dataMap, add it with default values
                    dataMap.set(appId, {
                        applicationId: appId,
                        name: app.name,
                        orderCompletionRate: 0,
                        conversationalSuccessRate: 0,
                        callTransferredRate: 0,
                        callImmediateTransferredRate: 0,
                        averageOrderTotal: 0
                    });
                }
            });
            if (array)
                array.forEach((item: { applicationId: string }) => {
                    const appId = item.applicationId;
                    if (!dataMap.has(appId)) {
                        dataMap.set(appId, { applicationId: appId });
                    }
                    dataMap.get(appId)[fieldName] = item[key] || "0";
                });
        };

        addDataToMap(orderCompletion.applications, "orderCompletionRate", "orderCompletionRate");
        addDataToMap(conversationalSuccess.applications, "conversationalSuccessRate", "conversationalSuccessRate");
        addDataToMap(callTransferred.applications, "callTransferredRate", "callTransferredRate");
        addDataToMap(callImmediateTransferred.applications, "callImmediateTransferredRate", "callImmediateTransferredRate");
        addDataToMap(averageOrderTotal.applications, "averageOrderTotal", "averageOrderTotal");

        // Convert the map back to an array
        const data = Array.from(dataMap.values());
        setOrgApplicationsPerformanceAnalytics(data)
        setLoadOrgApplicationsPerformanceAnalytics(false)
    }

    // #region Engagement Analytics Functions

    // Helper to flatten grouped engagements (API returns {applicationId: OrganizationEngagement[]})
    const flattenGroupedEngagements = (grouped: Record<string, voicifyApi.OrganizationEngagement[]> | null | undefined): voicifyApi.OrganizationEngagement[] => {
        if (!grouped || typeof grouped !== 'object') {
            console.warn('[flattenGroupedEngagements] Received invalid data:', grouped);
            return [];
        }
        return Object.values(grouped).flat();
    };

    // Default page size for engagement pagination - balance between fewer requests and memory usage
    // Increased to 500 for better performance with large datasets (fewer API roundtrips)
    const ENGAGEMENT_PAGE_SIZE = 5000;

    // Fetch every page by default so detail modals and exports have the complete result set.
    // Deployments can still set REACT_APP_MAX_ENGAGEMENTS_CAP as an emergency browser-memory guard.
    const configuredEngagementsCap = parseInt(process.env.REACT_APP_MAX_ENGAGEMENTS_CAP || '', 10);
    const MAX_ENGAGEMENTS_CAP = configuredEngagementsCap > 0 ? configuredEngagementsCap : Number.MAX_SAFE_INTEGER;

    // Organization Engagements - with pagination support for large datasets
    // Pattern based on SessionAnalyticsContainer.loadSessionsForOrgs
    // @param maxEngagementsCap - Optional cap to limit in-memory accumulation (defaults to MAX_ENGAGEMENTS_CAP)
    const loadOrganizationEngagements = async (
        organizationId: string,
        filter: EngagementAnalyticsFilter,
        maxEngagementsCap: number = MAX_ENGAGEMENTS_CAP
    ) => {
        setLoadingOrganizationEngagements(true);
        setError("");

        // Clear previous data to free memory before loading new data
        setOrganizationEngagements([]);
        setScheduledEngagements([]);
        setRescheduledEngagements([]);
        setCancelledEngagements([]);
        setCheckNextAppointmentEngagements([]);

        let allEngagements: voicifyApi.OrganizationEngagement[] = [];
        let skip = 0;
        const pageSize = filter.take ?? ENGAGEMENT_PAGE_SIZE;
        let hasMoreData = true;
        let reachedCap = false;

        try {
            const normalizedFilter = normalizeFilterOrThrow(filter);
            while (hasMoreData) {
                // Check if we've reached the cap before fetching more data
                if (allEngagements.length >= maxEngagementsCap) {
                    console.warn(
                        `[loadOrganizationEngagements] Reached maximum engagements cap (${maxEngagementsCap}). ` +
                        `Returning partial results with ${allEngagements.length} engagements. ` +
                        `Consider narrowing the date range or increasing REACT_APP_MAX_ENGAGEMENTS_CAP.`
                    );
                    reachedCap = true;
                    hasMoreData = false;
                    break;
                }

                const paginatedFilter: EngagementAnalyticsFilter = {
                    ...normalizedFilter,
                    skip,
                    take: pageSize
                };

                const result = await voicifyApi.getOrganizationEngagements(organizationId, paginatedFilter);

                if (result.resultType === "Ok") {
                    const pageEngagements = result.data || [];
                    allEngagements.push(...pageEngagements);

                    // Check if adding this page exceeded the cap
                    if (allEngagements.length >= maxEngagementsCap) {
                        console.warn(
                            `[loadOrganizationEngagements] Reached maximum engagements cap (${maxEngagementsCap}). ` +
                            `Returning partial results with ${allEngagements.length} engagements. ` +
                            `Consider narrowing the date range or increasing REACT_APP_MAX_ENGAGEMENTS_CAP.`
                        );
                        reachedCap = true;
                        hasMoreData = false;
                    } else if (pageEngagements.length < pageSize || pageEngagements.length === 0) {
                        // If we received fewer records than requested or none, we've reached the end
                        hasMoreData = false;
                    } else {
                        skip += pageSize;
                    }
                } else {
                    setError(result.errors?.[0] || 'Failed to load engagements');
                    hasMoreData = false;
                }
            }

            setOrganizationEngagements(allEngagements);

            // Return info about whether cap was reached (useful for callers)
            return { engagements: allEngagements, reachedCap, cap: maxEngagementsCap };
        } catch (error) {
            console.error('[loadOrganizationEngagements] Error:', error);
            setError(error?.message || 'Unknown error loading engagements');
            return { engagements: allEngagements, reachedCap: false, cap: maxEngagementsCap };
        } finally {
            setLoadingOrganizationEngagements(false);
        }
    };

    const loadPlatformEngagements = async (
        organizationIds: string[],
        filter: EngagementAnalyticsFilter,
        maxEngagementsCap: number = MAX_ENGAGEMENTS_CAP
    ) => {
        setLoadingPlatformEngagements(true);
        setError("");
        setPlatformEngagements([]);
        setPlatformEngagementsPerOrg({});

        let allGroupedEngagements: Record<string, OrganizationEngagement[]> = {};
        let allEngagements: OrganizationEngagement[] = [];
        let skip = 0;
        const pageSize = Math.min(filter.take ?? ENGAGEMENT_PAGE_SIZE, ENGAGEMENT_PAGE_SIZE);
        let hasMoreData = true;
        let reachedCap = false;

        try {
            const normalizedFilter = normalizeFilterOrThrow(filter);
            while (hasMoreData) {
                if (allEngagements.length >= maxEngagementsCap) {
                    reachedCap = true;
                    break;
                }

                const take = Math.min(pageSize, maxEngagementsCap - allEngagements.length);
                const result = await voicifyApi.getPlatformEngagements(organizationIds, {
                    ...normalizedFilter,
                    skip,
                    take
                });

                if (result.resultType !== "Ok") {
                    setError(result.errors?.[0] || 'Failed to load platform engagements');
                    break;
                }

                const pageGroupedEngagements = result.data || {};
                const pageEngagements = flattenGroupedEngagements(pageGroupedEngagements);
                Object.entries(pageGroupedEngagements).forEach(([organizationId, engagements]) => {
                    if (engagements.length === 0) return;
                    allGroupedEngagements[organizationId] = [
                        ...(allGroupedEngagements[organizationId] || []),
                        ...engagements
                    ];
                });
                allEngagements.push(...pageEngagements);

                // A response larger than requested indicates an older backend that ignored
                // pagination. Treat that legacy full response as complete instead of looping.
                if (pageEngagements.length !== take) {
                    hasMoreData = false;
                } else {
                    skip += pageEngagements.length;
                }
            }

            setPlatformEngagementsPerOrg(allGroupedEngagements);
            setPlatformEngagements(allEngagements);

            return { engagements: allEngagements, groupedEngagements: allGroupedEngagements, reachedCap, cap: maxEngagementsCap };
        } catch (error) {
            console.error('[loadPlatformEngagements] Error:', error);
            setError(error?.message || 'Unknown error loading platform engagements');
            return { engagements: allEngagements, groupedEngagements: allGroupedEngagements, reachedCap, cap: maxEngagementsCap };
        } finally {
            setLoadingPlatformEngagements(false);
        }
    };

    const loadEngagementsForTagCombo = async (
        organizationId: string,
        filter: EngagementAnalyticsFilter,
        category: string,
        validTags: string[] = [],
        excludedTags: string[] = []
    ) => {
        setLoadingEngagementsForTagCombo(true);
        try {
            const normalizedFilter = normalizeFilterOrThrow(filter);
            const result = await voicifyApi.getEngagementsForTagCombo(organizationId, normalizedFilter, category, validTags, excludedTags);
            if (result.resultType === "Ok") {
                setEngagementsForTagCombo(flattenGroupedEngagements(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize tag combo filter');
        } finally {
            setLoadingEngagementsForTagCombo(false);
        }
    };

    const loadPlatformEngagementsForTagCombo = async (
        organizationIds: string[],
        filter: EngagementAnalyticsFilter,
        category: string,
        validTags: string[] = [],
        excludedTags: string[] = []
    ) => {
        setLoadingPlatformEngagementsForTagCombo(true);
        try {
            const normalizedFilter = normalizeFilterOrThrow(filter);
            const result = await voicifyApi.getPlatformEngagementsForTagCombo(organizationIds, normalizedFilter, category, validTags, excludedTags);
            if (result.resultType === "Ok") {
                setPlatformEngagementsForTagCombo(flattenGroupedEngagements(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize platform tag combo filter');
        } finally {
            setLoadingPlatformEngagementsForTagCombo(false);
        }
    };

    // Off Hours Engagements
    const loadOffHoursEngagements = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingOffHoursEngagements(true);
        try {
            const normalizedFilter = normalizeFilterOrThrow(filter);
            const result = await voicifyApi.getEngagementsForTagCombo(organizationId, normalizedFilter, '', ['CALL_DURING_OFF_HOURS'], []);
            if (result.resultType === "Ok") {
                setOffHoursEngagements(flattenGroupedEngagements(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize off-hours filter');
        } finally {
            setLoadingOffHoursEngagements(false);
        }
    };

    // Spanish Off Hours Engagements (for language breakdown using CALL_DURING_OFF_HOURS + SPANISH tags)
    const loadSpanishOffHoursEngagements = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingSpanishOffHoursEngagements(true);
        try {
            const normalizedFilter = normalizeFilterOrThrow(filter);
            const result = await voicifyApi.getEngagementsForTagCombo(organizationId, normalizedFilter, '', ['CALL_DURING_OFF_HOURS', 'SPANISH'], []);
            if (result.resultType === "Ok") {
                setSpanishOffHoursEngagements(flattenGroupedEngagements(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : "Failed to load Spanish off-hours engagements");
        } finally {
            setLoadingSpanishOffHoursEngagements(false);
        }
    };

    // Transferred Engagements (for Spanish-level transfer data via cross-referencing)
    const loadTransferredEngagements = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingTransferredEngagements(true);
        try {
            const normalizedFilter = normalizeFilterOrThrow(filter);
            const result = await voicifyApi.getEngagementsForTagCombo(organizationId, normalizedFilter, '', ['TRANSFERRED'], []);
            if (result.resultType === "Ok") {
                setTransferredEngagements(flattenGroupedEngagements(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize transferred engagements filter');
        } finally {
            setLoadingTransferredEngagements(false);
        }
    };

    // Top Level Metrics
    const loadTopLevelMetrics = async (applicationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingTopLevelMetrics(true);
        const result = await voicifyApi.getTopLevelMetrics(applicationId, filter);
        setLoadingTopLevelMetrics(false);
        if (result.resultType === "Ok") {
            setTopLevelMetrics(result.data);
        } else {
            setError(result.errors?.[0]);
        }
    };

    const loadOrganizationTopLevelMetrics = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingOrganizationTopLevelMetrics(true);
        try {
            const normalizedFilter = normalizeFilterOrThrow(filter);
            const result = await voicifyApi.getOrganizationTopLevelMetrics(organizationId, normalizedFilter);
            if (result.resultType === "Ok") {
                // API returns Record<applicationId, TopLevelMetrics> - store raw per-app data
                const appMetricsMap = result.data as Record<string, TopLevelMetrics>;
                setOrganizationTopLevelMetricsPerApp(appMetricsMap);

                // Also aggregate across all apps for backward compatibility
                const aggregatedMetrics: TopLevelMetrics = {
                    laborMinutesSaved: 0,
                    laborHoursSaved: 0,
                    totalCalls: 0,
                    containedCalls: 0,
                    containableCalls: 0,
                    offHoursCalls: 0,
                    offHoursScheduledAppts: 0,
                    newPatientBookings: 0,
                    existingPatientBookings: 0,
                    totalBookings: 0,
                    orderAttempts: 0,
                    ordersPlaced: 0,
                    upsellAttempts: 0,
                    upsellSuccesses: 0,
                    questionsAnswered: 0,
                    averageOrderTotal: 0,
                    laborHoursSavedApps: [],
                    offHoursApps: [],
                    offHoursScheduledApptsApps: [],
                    newPatientBookingsApps: [],
                    ticketApps: [],
                    upsellApps: [],
                    questionsApps: []
                };

                // Track weighted average for averageOrderTotal
                let totalOrdersForAverage = 0;
                let weightedOrderTotalSum = 0;

                for (const [appId, metrics] of Object.entries(appMetricsMap)) {
                    if (metrics) {
                        aggregatedMetrics.laborMinutesSaved += metrics.laborMinutesSaved ?? 0;
                        aggregatedMetrics.totalCalls += metrics.totalEngagementCount ?? metrics.totalCalls ?? 0;
                        // Contained calls: use generic containedCalls if present, otherwise scheduling-specific fields,
                        // and booking fields only as a final fallback (not added on top)
                        const schedulingContainedCalls = (metrics.totalContainedCountEnglish ?? 0) + (metrics.totalContainedCountSpanish ?? 0);
                        const bookingContainedFallback = (metrics.totalBookings ?? 0) || (metrics.existingPatientBookings ?? 0);
                        if (metrics.containedCalls != null && metrics.containedCalls > 0) {
                            aggregatedMetrics.containedCalls += metrics.containedCalls;
                        } else if (schedulingContainedCalls > 0) {
                            aggregatedMetrics.containedCalls += schedulingContainedCalls;
                        } else {
                            aggregatedMetrics.containedCalls += bookingContainedFallback;
                        }
                        // Containable calls: use generic containableCalls if present, otherwise scheduling attempt fields,
                        // and booking fields only as a final fallback (not added on top)
                        const schedulingContainableCalls =
                            (metrics.totalScheduleAttemptsEnglish ?? 0) + (metrics.totalScheduleAttemptsSpanish ?? 0) +
                            (metrics.totalRescheduleAttemptsEnglish ?? 0) + (metrics.totalRescheduleAttemptsSpanish ?? 0) +
                            (metrics.totalCancellationAttemptsEnglish ?? 0) + (metrics.totalCancellationAttemptsSpanish ?? 0) +
                            (metrics.totalCheckNextAppointmentAttemptsEnglish ?? 0) + (metrics.totalCheckNextAppointmentAttemptsSpanish ?? 0);
                        const bookingContainableFallback = (metrics.totalBookings ?? 0) || (metrics.existingPatientBookings ?? 0);
                        if (metrics.containableCalls != null && metrics.containableCalls > 0) {
                            aggregatedMetrics.containableCalls += metrics.containableCalls;
                        } else if (schedulingContainableCalls > 0) {
                            aggregatedMetrics.containableCalls += schedulingContainableCalls;
                        } else {
                            aggregatedMetrics.containableCalls += bookingContainableFallback;
                        }
                        aggregatedMetrics.offHoursCalls += metrics.offHoursCalls ?? 0;
                        aggregatedMetrics.offHoursScheduledAppts += metrics.offHoursScheduledAppts ?? 0;
                        aggregatedMetrics.newPatientBookings += (metrics.newPatientBookedEnglish ?? 0) + (metrics.newPatientBookedSpanish ?? 0);
                        aggregatedMetrics.orderAttempts += metrics.totalOrderAttempts ?? metrics.orderAttempts ?? 0;
                        aggregatedMetrics.ordersPlaced += metrics.totalOrdersCompleted ?? metrics.ordersPlaced ?? 0;
                        aggregatedMetrics.upsellAttempts += metrics.totalUpsellAttempts ?? metrics.upsellAttempts ?? 0;
                        aggregatedMetrics.upsellSuccesses += metrics.totalUpsellsCompleted ?? metrics.upsellSuccesses ?? 0;
                        aggregatedMetrics.questionsAnswered += metrics.totalQuestionsAnswered ?? metrics.questionsAnswered ?? 0;

                        // Aggregate averageOrderTotal as weighted average
                        // Handle various field names from API: averageTicketSize (actual API field), averageOrderTotal, etc.
                        const appOrderTotal = metrics.averageTicketSize ?? metrics.averageOrderTotal ?? metrics.AverageOrderTotal ?? metrics.totalAverageOrderValue ?? metrics.TotalAverageOrderValue ?? 0;
                        const appOrderCount = metrics.totalOrdersCompleted ?? metrics.TotalOrdersCompleted ?? metrics.ordersPlaced ?? metrics.OrdersPlaced ?? 0;
                        if (appOrderTotal > 0) {
                            if (appOrderCount > 0) {
                                weightedOrderTotalSum += appOrderTotal * appOrderCount;
                                totalOrdersForAverage += appOrderCount;
                            } else {
                                // If we have an average but no order count, just use the average directly
                                // This handles cases where only averageOrderTotal is provided
                                weightedOrderTotalSum += appOrderTotal;
                                totalOrdersForAverage += 1;
                            }
                        }

                        // Add app breakdown for laborHoursSaved
                        const laborMinutesEnglish = metrics.laborMinutesSavedEnglish ?? 0;
                        const laborMinutesSpanish = metrics.laborMinutesSavedSpanish ?? 0;
                        const laborMinutes = (laborMinutesEnglish + laborMinutesSpanish) || (metrics.laborMinutesSaved ?? 0);
                        if (laborMinutes > 0) {
                            aggregatedMetrics.laborHoursSavedApps.push({
                                applicationId: appId,
                                applicationName: appId, // Will be resolved by transformer using appNameMap
                                count: laborMinutes / 60, // Convert to hours
                                english: laborMinutesEnglish / 60,
                                spanish: laborMinutesSpanish / 60
                            });
                        }

                        // Add app breakdown for newPatientBookings
                        const englishCount = metrics.newPatientBookedEnglish ?? 0;
                        const spanishCount = metrics.newPatientBookedSpanish ?? 0;
                        const totalCount = englishCount + spanishCount;
                        if (totalCount > 0) {
                            aggregatedMetrics.newPatientBookingsApps.push({
                                applicationId: appId,
                                applicationName: appId, // Will be resolved by transformer
                                count: totalCount,
                                english: englishCount,
                                spanish: spanishCount
                            });
                        }

                        // Add app breakdown for ticketApps (average order total per app)
                        // Handle various field names from API: averageTicketSize (actual API field), averageOrderTotal, etc.
                        const ticketAppOrderTotal = metrics.averageTicketSize ?? metrics.averageOrderTotal ?? metrics.AverageOrderTotal ?? metrics.totalAverageOrderValue ?? metrics.TotalAverageOrderValue ?? 0;
                        if (ticketAppOrderTotal > 0) {
                            aggregatedMetrics.ticketApps.push({
                                applicationId: appId,
                                applicationName: appId, // Will be resolved by transformer using appNameMap
                                count: ticketAppOrderTotal
                            });
                        }

                        // Add app breakdown for upsellApps (successful upsells per app)
                        const upsellSuccessCount = metrics.totalUpsellsCompleted ?? metrics.upsellSuccesses ?? 0;
                        if (upsellSuccessCount > 0) {
                            aggregatedMetrics.upsellApps.push({
                                applicationId: appId,
                                applicationName: appId, // Will be resolved by transformer using appNameMap
                                count: upsellSuccessCount
                            });
                        }

                        // Add app breakdown for questionsApps (questions answered per app)
                        const questionsCount = metrics.totalQuestionsAnswered ?? metrics.questionsAnswered ?? 0;
                        if (questionsCount > 0) {
                            aggregatedMetrics.questionsApps.push({
                                applicationId: appId,
                                applicationName: appId, // Will be resolved by transformer using appNameMap
                                count: questionsCount
                            });
                        }
                    }
                }

                // Convert laborMinutesSaved to laborHoursSaved
                aggregatedMetrics.laborHoursSaved = aggregatedMetrics.laborMinutesSaved / 60;

                // Calculate weighted average for averageOrderTotal
                aggregatedMetrics.averageOrderTotal = totalOrdersForAverage > 0
                    ? weightedOrderTotalSum / totalOrdersForAverage
                    : 0;

                setOrganizationTopLevelMetrics(aggregatedMetrics);
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize top-level metrics filter');
        } finally {
            setLoadingOrganizationTopLevelMetrics(false);
        }
    };

    const aggregateGroupedTopLevelMetrics = (metricsMap: GroupedTopLevelMetrics): TopLevelMetrics => {
        const aggregatedMetrics: TopLevelMetrics = {
            laborMinutesSaved: 0,
            laborHoursSaved: 0,
            totalCalls: 0,
            containedCalls: 0,
            containableCalls: 0,
            offHoursCalls: 0,
            offHoursScheduledAppts: 0,
            newPatientBookings: 0,
            existingPatientBookings: 0,
            totalBookings: 0,
            orderAttempts: 0,
            ordersPlaced: 0,
            upsellAttempts: 0,
            upsellSuccesses: 0,
            questionsAnswered: 0,
            averageOrderTotal: 0,
            laborHoursSavedApps: [],
            offHoursApps: [],
            offHoursScheduledApptsApps: [],
            newPatientBookingsApps: [],
            ticketApps: [],
            upsellApps: [],
            questionsApps: []
        };

        let totalOrdersForAverage = 0;
        let weightedOrderTotalSum = 0;

        for (const [entityId, metrics] of Object.entries(metricsMap || {})) {
            if (!metrics) {
                continue;
            }

            aggregatedMetrics.laborMinutesSaved += metrics.laborMinutesSaved ?? 0;
            aggregatedMetrics.totalCalls += metrics.totalEngagementCount ?? metrics.totalCalls ?? 0;

            const schedulingContainedCalls = (metrics.totalContainedCountEnglish ?? 0) + (metrics.totalContainedCountSpanish ?? 0);
            const bookingContainedFallback = (metrics.totalBookings ?? 0) || (metrics.existingPatientBookings ?? 0);
            if (metrics.containedCalls != null && metrics.containedCalls > 0) {
                aggregatedMetrics.containedCalls += metrics.containedCalls;
            } else if (schedulingContainedCalls > 0) {
                aggregatedMetrics.containedCalls += schedulingContainedCalls;
            } else {
                aggregatedMetrics.containedCalls += bookingContainedFallback;
            }

            const schedulingContainableCalls =
                (metrics.totalScheduleAttemptsEnglish ?? 0) + (metrics.totalScheduleAttemptsSpanish ?? 0) +
                (metrics.totalRescheduleAttemptsEnglish ?? 0) + (metrics.totalRescheduleAttemptsSpanish ?? 0) +
                (metrics.totalCancellationAttemptsEnglish ?? 0) + (metrics.totalCancellationAttemptsSpanish ?? 0) +
                (metrics.totalCheckNextAppointmentAttemptsEnglish ?? 0) + (metrics.totalCheckNextAppointmentAttemptsSpanish ?? 0);
            const bookingContainableFallback = (metrics.totalBookings ?? 0) || (metrics.existingPatientBookings ?? 0);
            if (metrics.containableCalls != null && metrics.containableCalls > 0) {
                aggregatedMetrics.containableCalls += metrics.containableCalls;
            } else if (schedulingContainableCalls > 0) {
                aggregatedMetrics.containableCalls += schedulingContainableCalls;
            } else {
                aggregatedMetrics.containableCalls += bookingContainableFallback;
            }

            aggregatedMetrics.offHoursCalls += metrics.offHoursCalls ?? 0;
            aggregatedMetrics.offHoursScheduledAppts += metrics.offHoursScheduledAppts ?? 0;
            aggregatedMetrics.newPatientBookings += (metrics.newPatientBookedEnglish ?? 0) + (metrics.newPatientBookedSpanish ?? 0);
            aggregatedMetrics.orderAttempts += metrics.totalOrderAttempts ?? metrics.orderAttempts ?? 0;
            aggregatedMetrics.ordersPlaced += metrics.totalOrdersCompleted ?? metrics.ordersPlaced ?? 0;
            aggregatedMetrics.upsellAttempts += metrics.totalUpsellAttempts ?? metrics.upsellAttempts ?? 0;
            aggregatedMetrics.upsellSuccesses += metrics.totalUpsellsCompleted ?? metrics.upsellSuccesses ?? 0;
            aggregatedMetrics.questionsAnswered += metrics.totalQuestionsAnswered ?? metrics.questionsAnswered ?? 0;

            const appOrderTotal = metrics.averageTicketSize ?? metrics.averageOrderTotal ?? metrics.AverageOrderTotal ?? metrics.totalAverageOrderValue ?? metrics.TotalAverageOrderValue ?? 0;
            const appOrderCount = metrics.totalOrdersCompleted ?? metrics.TotalOrdersCompleted ?? metrics.ordersPlaced ?? metrics.OrdersPlaced ?? 0;
            if (appOrderTotal > 0) {
                if (appOrderCount > 0) {
                    weightedOrderTotalSum += appOrderTotal * appOrderCount;
                    totalOrdersForAverage += appOrderCount;
                } else {
                    weightedOrderTotalSum += appOrderTotal;
                    totalOrdersForAverage += 1;
                }
            }

            const laborMinutesEnglish = metrics.laborMinutesSavedEnglish ?? 0;
            const laborMinutesSpanish = metrics.laborMinutesSavedSpanish ?? 0;
            const laborMinutes = (laborMinutesEnglish + laborMinutesSpanish) || (metrics.laborMinutesSaved ?? 0);
            if (laborMinutes > 0) {
                aggregatedMetrics.laborHoursSavedApps.push({
                    applicationId: entityId,
                    applicationName: entityId,
                    count: laborMinutes / 60,
                    english: laborMinutesEnglish / 60,
                    spanish: laborMinutesSpanish / 60
                });
            }

            const englishCount = metrics.newPatientBookedEnglish ?? 0;
            const spanishCount = metrics.newPatientBookedSpanish ?? 0;
            const totalCount = englishCount + spanishCount;
            if (totalCount > 0) {
                aggregatedMetrics.newPatientBookingsApps.push({
                    applicationId: entityId,
                    applicationName: entityId,
                    count: totalCount,
                    english: englishCount,
                    spanish: spanishCount
                });
            }

            const ticketAppOrderTotal = metrics.averageTicketSize ?? metrics.averageOrderTotal ?? metrics.AverageOrderTotal ?? metrics.totalAverageOrderValue ?? metrics.TotalAverageOrderValue ?? 0;
            if (ticketAppOrderTotal > 0) {
                aggregatedMetrics.ticketApps.push({
                    applicationId: entityId,
                    applicationName: entityId,
                    count: ticketAppOrderTotal
                });
            }

            const upsellSuccessCount = metrics.totalUpsellsCompleted ?? metrics.upsellSuccesses ?? 0;
            if (upsellSuccessCount > 0) {
                aggregatedMetrics.upsellApps.push({
                    applicationId: entityId,
                    applicationName: entityId,
                    count: upsellSuccessCount
                });
            }

            const questionsCount = metrics.totalQuestionsAnswered ?? metrics.questionsAnswered ?? 0;
            if (questionsCount > 0) {
                aggregatedMetrics.questionsApps.push({
                    applicationId: entityId,
                    applicationName: entityId,
                    count: questionsCount
                });
            }
        }

        aggregatedMetrics.laborHoursSaved = aggregatedMetrics.laborMinutesSaved / 60;
        aggregatedMetrics.averageOrderTotal = totalOrdersForAverage > 0
            ? weightedOrderTotalSum / totalOrdersForAverage
            : 0;

        return aggregatedMetrics;
    };

    const loadPlatformTopLevelMetrics = async (organizationIds: string[], filter: EngagementAnalyticsFilter) => {
        setLoadingPlatformTopLevelMetrics(true);
        try {
            const normalizedFilter = normalizeFilterOrThrow(filter);
            const result = await voicifyApi.getPlatformTopLevelMetrics(organizationIds, normalizedFilter);
            if (result.resultType === "Ok") {
                const metricsMap = result.data || {};
                setPlatformTopLevelMetricsPerOrg(metricsMap);
                setPlatformTopLevelMetrics(aggregateGroupedTopLevelMetrics(metricsMap));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize platform top-level metrics filter');
        } finally {
            setLoadingPlatformTopLevelMetrics(false);
        }
    };

    const loadPlatformUpsoldRevenue = async (organizationIds: string[], filter: EngagementAnalyticsFilter) => {
        setLoadingPlatformUpsoldRevenue(true);
        try {
            const normalizedFilter = normalizeFilterOrThrow(filter);
            const result = await voicifyApi.getPlatformUpsoldRevenue(organizationIds, normalizedFilter);
            if (result.resultType === "Ok") {
                setPlatformUpsoldRevenuePerOrg(result.data || {});
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize platform upsold revenue filter');
        } finally {
            setLoadingPlatformUpsoldRevenue(false);
        }
    };

    // Average Call Volume Histograms
    const loadAverageCallVolumeHistogram = async (applicationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingAverageCallVolumeHistogram(true);
        const result = await voicifyApi.getAverageCallVolumeHistogram(applicationId, filter);
        setLoadingAverageCallVolumeHistogram(false);
        if (result.resultType === "Ok") {
            setAverageCallVolumeHistogram(result.data);
        } else {
            setError(result.errors?.[0]);
        }
    };

    const loadOrganizationAverageCallVolumeHistogram = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingOrganizationAverageCallVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getOrganizationAverageCallVolumeHistogram(organizationId, normalizedFilter);
            if (result.resultType === "Ok") {
                setOrganizationAverageCallVolumeHistogramPerApp(result.data);
                setOrganizationAverageCallVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize average call volume filter');
        } finally {
            setLoadingOrganizationAverageCallVolumeHistogram(false);
        }
    };

    const loadPlatformAverageCallVolumeHistogram = async (organizationIds: string[], filter: EngagementAnalyticsFilter) => {
        setLoadingPlatformAverageCallVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getPlatformAverageCallVolumeHistogram(organizationIds, normalizedFilter);
            if (result.resultType === "Ok") {
                setPlatformAverageCallVolumeHistogramPerOrg(result.data);
                setPlatformAverageCallVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize platform average call volume filter');
        } finally {
            setLoadingPlatformAverageCallVolumeHistogram(false);
        }
    };

    const loadOrganizationAverageCallVolumeByHourHistogram = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingOrganizationAverageCallVolumeByHourHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getOrganizationAverageCallVolumeHistogram(organizationId, normalizedFilter);
            if (result.resultType === "Ok") {
                setOrganizationAverageCallVolumeByHourHistogramPerApp(result.data);
                setOrganizationAverageCallVolumeByHourHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize average-by-hour filter');
        } finally {
            setLoadingOrganizationAverageCallVolumeByHourHistogram(false);
        }
    };

    const loadPlatformAverageCallVolumeByHourHistogram = async (organizationIds: string[], filter: EngagementAnalyticsFilter) => {
        setLoadingPlatformAverageCallVolumeByHourHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getPlatformAverageCallVolumeHistogram(organizationIds, normalizedFilter);
            if (result.resultType === "Ok") {
                setPlatformAverageCallVolumeByHourHistogramPerOrg(result.data);
                setPlatformAverageCallVolumeByHourHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize platform average-by-hour filter');
        } finally {
            setLoadingPlatformAverageCallVolumeByHourHistogram(false);
        }
    };

    const loadAverageContainableCallVolumeHistogram = async (applicationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingAverageContainableCallVolumeHistogram(true);
        const result = await voicifyApi.getAverageContainableCallVolumeHistogram(applicationId, filter);
        setLoadingAverageContainableCallVolumeHistogram(false);
        if (result.resultType === "Ok") {
            setAverageContainableCallVolumeHistogram(result.data);
        } else {
            setError(result.errors?.[0]);
        }
    };

    const loadOrganizationAverageContainableCallVolumeHistogram = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingOrganizationAverageContainableCallVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getOrganizationAverageContainableCallVolumeHistogram(organizationId, normalizedFilter);
            if (result.resultType === "Ok") {
                setOrganizationAverageContainableCallVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize average containable filter');
        } finally {
            setLoadingOrganizationAverageContainableCallVolumeHistogram(false);
        }
    };

    const loadAverageContainedCallVolumeHistogram = async (applicationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingAverageContainedCallVolumeHistogram(true);
        const result = await voicifyApi.getAverageContainedCallVolumeHistogram(applicationId, filter);
        setLoadingAverageContainedCallVolumeHistogram(false);
        if (result.resultType === "Ok") {
            setAverageContainedCallVolumeHistogram(result.data);
        } else {
            setError(result.errors?.[0]);
        }
    };

    const loadOrganizationAverageContainedCallVolumeHistogram = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingOrganizationAverageContainedCallVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getOrganizationAverageContainedCallVolumeHistogram(organizationId, normalizedFilter);
            if (result.resultType === "Ok") {
                setOrganizationAverageContainedCallVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize average contained filter');
        } finally {
            setLoadingOrganizationAverageContainedCallVolumeHistogram(false);
        }
    };

    // Call Volume Histograms
    const loadCallVolumeHistogram = async (applicationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingCallVolumeHistogram(true);
        const result = await voicifyApi.getCallVolumeHistogram(applicationId, filter);
        setLoadingCallVolumeHistogram(false);
        if (result.resultType === "Ok") {
            setCallVolumeHistogram(result.data);
        } else {
            setError(result.errors?.[0]);
        }
    };

    const loadOrganizationCallVolumeHistogram = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingOrganizationCallVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getOrganizationCallVolumeHistogram(organizationId, normalizedFilter);
            if (result.resultType === "Ok") {
                // Store raw per-app data for filtering
                setOrganizationCallVolumeHistogramPerApp(result.data);
                // Also flatten for backward compatibility
                setOrganizationCallVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize call volume filter');
        } finally {
            setLoadingOrganizationCallVolumeHistogram(false);
        }
    };

    const loadPlatformCallVolumeHistogram = async (organizationIds: string[], filter: EngagementAnalyticsFilter) => {
        setLoadingPlatformCallVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getPlatformCallVolumeHistogram(organizationIds, normalizedFilter);
            if (result.resultType === "Ok") {
                setPlatformCallVolumeHistogramPerOrg(result.data);
                setPlatformCallVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize platform call volume filter');
        } finally {
            setLoadingPlatformCallVolumeHistogram(false);
        }
    };

    const loadContainableCallVolumeHistogram = async (applicationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingContainableCallVolumeHistogram(true);
        const result = await voicifyApi.getContainableCallVolumeHistogram(applicationId, filter);
        setLoadingContainableCallVolumeHistogram(false);
        if (result.resultType === "Ok") {
            setContainableCallVolumeHistogram(result.data);
        } else {
            setError(result.errors?.[0]);
        }
    };

    const loadOrganizationContainableCallVolumeHistogram = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingOrganizationContainableCallVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getOrganizationContainableCallVolumeHistogram(organizationId, normalizedFilter);
            if (result.resultType === "Ok") {
                setOrganizationContainableCallVolumeHistogramPerApp(result.data);
                setOrganizationContainableCallVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize containable call volume filter');
        } finally {
            setLoadingOrganizationContainableCallVolumeHistogram(false);
        }
    };

    const loadPlatformContainableCallVolumeHistogram = async (organizationIds: string[], filter: EngagementAnalyticsFilter) => {
        setLoadingPlatformContainableCallVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getPlatformContainableCallVolumeHistogram(organizationIds, normalizedFilter);
            if (result.resultType === "Ok") {
                setPlatformContainableCallVolumeHistogramPerOrg(result.data);
                setPlatformContainableCallVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize platform containable call volume filter');
        } finally {
            setLoadingPlatformContainableCallVolumeHistogram(false);
        }
    };

    const loadContainedCallVolumeHistogram = async (applicationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingContainedCallVolumeHistogram(true);
        const result = await voicifyApi.getContainedCallVolumeHistogram(applicationId, filter);
        setLoadingContainedCallVolumeHistogram(false);
        if (result.resultType === "Ok") {
            setContainedCallVolumeHistogram(result.data);
        } else {
            setError(result.errors?.[0]);
        }
    };

    const loadOrganizationContainedCallVolumeHistogram = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingOrganizationContainedCallVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getOrganizationContainedCallVolumeHistogram(organizationId, normalizedFilter);
            if (result.resultType === "Ok") {
                setOrganizationContainedCallVolumeHistogramPerApp(result.data);
                setOrganizationContainedCallVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize contained call volume filter');
        } finally {
            setLoadingOrganizationContainedCallVolumeHistogram(false);
        }
    };

    const loadPlatformContainedCallVolumeHistogram = async (organizationIds: string[], filter: EngagementAnalyticsFilter) => {
        setLoadingPlatformContainedCallVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getPlatformContainedCallVolumeHistogram(organizationIds, normalizedFilter);
            if (result.resultType === "Ok") {
                setPlatformContainedCallVolumeHistogramPerOrg(result.data);
                setPlatformContainedCallVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize platform contained call volume filter');
        } finally {
            setLoadingPlatformContainedCallVolumeHistogram(false);
        }
    };

    // Spanish Call Volume Histograms
    const loadSpanishCallVolumeHistogram = async (applicationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingSpanishCallVolumeHistogram(true);
        const result = await voicifyApi.getSpanishCallVolumeHistogram(applicationId, filter);
        setLoadingSpanishCallVolumeHistogram(false);
        if (result.resultType === "Ok") {
            setSpanishCallVolumeHistogram(result.data);
        } else {
            setError(result.errors?.[0]);
        }
    };

    const loadOrganizationSpanishCallVolumeHistogram = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingOrganizationSpanishCallVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getOrganizationSpanishCallVolumeHistogram(organizationId, normalizedFilter);
            if (result.resultType === "Ok") {
                setOrganizationSpanishCallVolumeHistogramPerApp(result.data);
                setOrganizationSpanishCallVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize Spanish call volume filter');
        } finally {
            setLoadingOrganizationSpanishCallVolumeHistogram(false);
        }
    };

    const loadPlatformSpanishCallVolumeHistogram = async (organizationIds: string[], filter: EngagementAnalyticsFilter) => {
        setLoadingPlatformSpanishCallVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getPlatformSpanishCallVolumeHistogram(organizationIds, normalizedFilter);
            if (result.resultType === "Ok") {
                setPlatformSpanishCallVolumeHistogramPerOrg(result.data);
                setPlatformSpanishCallVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize platform Spanish call volume filter');
        } finally {
            setLoadingPlatformSpanishCallVolumeHistogram(false);
        }
    };

    const loadSpanishContainableCallVolumeHistogram = async (applicationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingSpanishContainableCallVolumeHistogram(true);
        const result = await voicifyApi.getSpanishContainableCallVolumeHistogram(applicationId, filter);
        setLoadingSpanishContainableCallVolumeHistogram(false);
        if (result.resultType === "Ok") {
            setSpanishContainableCallVolumeHistogram(result.data);
        } else {
            setError(result.errors?.[0]);
        }
    };

    const loadOrganizationSpanishContainableCallVolumeHistogram = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingOrganizationSpanishContainableCallVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getOrganizationSpanishContainableCallVolumeHistogram(organizationId, normalizedFilter);
            if (result.resultType === "Ok") {
                setOrganizationSpanishContainableCallVolumeHistogramPerApp(result.data);
                setOrganizationSpanishContainableCallVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize Spanish containable filter');
        } finally {
            setLoadingOrganizationSpanishContainableCallVolumeHistogram(false);
        }
    };

    const loadPlatformSpanishContainableCallVolumeHistogram = async (organizationIds: string[], filter: EngagementAnalyticsFilter) => {
        setLoadingPlatformSpanishContainableCallVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getPlatformSpanishContainableCallVolumeHistogram(organizationIds, normalizedFilter);
            if (result.resultType === "Ok") {
                setPlatformSpanishContainableCallVolumeHistogramPerOrg(result.data);
                setPlatformSpanishContainableCallVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize platform Spanish containable filter');
        } finally {
            setLoadingPlatformSpanishContainableCallVolumeHistogram(false);
        }
    };

    const loadSpanishContainedCallVolumeHistogram = async (applicationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingSpanishContainedCallVolumeHistogram(true);
        const result = await voicifyApi.getSpanishContainedCallVolumeHistogram(applicationId, filter);
        setLoadingSpanishContainedCallVolumeHistogram(false);
        if (result.resultType === "Ok") {
            setSpanishContainedCallVolumeHistogram(result.data);
        } else {
            setError(result.errors?.[0]);
        }
    };

    const loadOrganizationSpanishContainedCallVolumeHistogram = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingOrganizationSpanishContainedCallVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getOrganizationSpanishContainedCallVolumeHistogram(organizationId, normalizedFilter);
            if (result.resultType === "Ok") {
                setOrganizationSpanishContainedCallVolumeHistogramPerApp(result.data);
                setOrganizationSpanishContainedCallVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize Spanish contained filter');
        } finally {
            setLoadingOrganizationSpanishContainedCallVolumeHistogram(false);
        }
    };

    const loadPlatformSpanishContainedCallVolumeHistogram = async (organizationIds: string[], filter: EngagementAnalyticsFilter) => {
        setLoadingPlatformSpanishContainedCallVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getPlatformSpanishContainedCallVolumeHistogram(organizationIds, normalizedFilter);
            if (result.resultType === "Ok") {
                setPlatformSpanishContainedCallVolumeHistogramPerOrg(result.data);
                setPlatformSpanishContainedCallVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize platform Spanish contained filter');
        } finally {
            setLoadingPlatformSpanishContainedCallVolumeHistogram(false);
        }
    };

    // Concurrent Call Volume Histograms
    const loadConcurrentCallVolumeHistogram = async (applicationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingConcurrentCallVolumeHistogram(true);
        const result = await voicifyApi.getConcurrentCallVolumeHistogram(applicationId, filter);
        setLoadingConcurrentCallVolumeHistogram(false);
        if (result.resultType === "Ok") {
            setConcurrentCallVolumeHistogram(result.data);
        } else {
            setError(result.errors?.[0]);
        }
    };

    const loadOrganizationConcurrentCallVolumeHistogram = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingOrganizationConcurrentCallVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getOrganizationConcurrentCallVolumeHistogram(organizationId, normalizedFilter);
            if (result.resultType === "Ok") {
                setOrganizationConcurrentCallVolumeHistogramPerApp(result.data);
                // Use max-based aggregation for concurrent calls to avoid inflated values
                setOrganizationConcurrentCallVolumeHistogram(flattenConcurrentCallHistogramDataFiltered(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize concurrent call filter');
        } finally {
            setLoadingOrganizationConcurrentCallVolumeHistogram(false);
        }
    };

    const loadPlatformConcurrentCallVolumeHistogram = async (organizationIds: string[], filter: EngagementAnalyticsFilter) => {
        setLoadingPlatformConcurrentCallVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getPlatformConcurrentCallVolumeHistogram(organizationIds, normalizedFilter);
            if (result.resultType === "Ok") {
                setPlatformConcurrentCallVolumeHistogramPerOrg(result.data);
                setPlatformConcurrentCallVolumeHistogram(flattenConcurrentCallHistogramDataFiltered(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize platform concurrent call filter');
        } finally {
            setLoadingPlatformConcurrentCallVolumeHistogram(false);
        }
    };

    // #endregion

    // #region Order Volume Histograms

    const loadOrderAttemptedVolumeHistogram = async (applicationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingOrderAttemptedVolumeHistogram(true);
        const result = await voicifyApi.getOrderAttemptedVolume(applicationId, filter);
        setLoadingOrderAttemptedVolumeHistogram(false);
        if (result.resultType === "Ok") {
            setOrderAttemptedVolumeHistogram(result.data);
        } else {
            setError(result.errors?.[0]);
        }
    };

    const loadOrganizationOrderAttemptedVolumeHistogram = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingOrganizationOrderAttemptedVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getOrganizationOrderAttemptedVolume(organizationId, normalizedFilter);
            if (result.resultType === "Ok") {
                setOrganizationOrderAttemptedVolumeHistogramPerApp(result.data);
                setOrganizationOrderAttemptedVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize order-attempted filter');
        } finally {
            setLoadingOrganizationOrderAttemptedVolumeHistogram(false);
        }
    };

    const loadPlatformOrderAttemptedVolumeHistogram = async (organizationIds: string[], filter: EngagementAnalyticsFilter) => {
        setLoadingPlatformOrderAttemptedVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getPlatformOrderAttemptedVolume(organizationIds, normalizedFilter);
            if (result.resultType === "Ok") {
                setPlatformOrderAttemptedVolumeHistogramPerOrg(result.data);
                setPlatformOrderAttemptedVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize platform order-attempted filter');
        } finally {
            setLoadingPlatformOrderAttemptedVolumeHistogram(false);
        }
    };

    const loadOrderPlacedVolumeHistogram = async (applicationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingOrderPlacedVolumeHistogram(true);
        const result = await voicifyApi.getOrderPlacedVolume(applicationId, filter);
        setLoadingOrderPlacedVolumeHistogram(false);
        if (result.resultType === "Ok") {
            setOrderPlacedVolumeHistogram(result.data);
        } else {
            setError(result.errors?.[0]);
        }
    };

    const loadOrganizationOrderPlacedVolumeHistogram = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingOrganizationOrderPlacedVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getOrganizationOrderPlacedVolume(organizationId, normalizedFilter);
            if (result.resultType === "Ok") {
                setOrganizationOrderPlacedVolumeHistogramPerApp(result.data);
                setOrganizationOrderPlacedVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize order-placed filter');
        } finally {
            setLoadingOrganizationOrderPlacedVolumeHistogram(false);
        }
    };

    const loadPlatformOrderPlacedVolumeHistogram = async (organizationIds: string[], filter: EngagementAnalyticsFilter) => {
        setLoadingPlatformOrderPlacedVolumeHistogram(true);
        try {
            const normalizedFilter = normalizeHistogramFilterOrThrow(filter);
            const result = await voicifyApi.getPlatformOrderPlacedVolume(organizationIds, normalizedFilter);
            if (result.resultType === "Ok") {
                setPlatformOrderPlacedVolumeHistogramPerOrg(result.data);
                setPlatformOrderPlacedVolumeHistogram(flattenGroupedHistogramData(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize platform order-placed filter');
        } finally {
            setLoadingPlatformOrderPlacedVolumeHistogram(false);
        }
    };

    // #endregion

    // #region Transfer Reasons

    const loadOrganizationTransferReasons = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingOrganizationTransferReasons(true);
        try {
            const normalizedFilter = normalizeFilterOrThrow(filter);
            const plannedResult = await voicifyApi.getOrganizationLevelTransferReasons(organizationId, normalizedFilter, true);
            const unplannedResult = await voicifyApi.getOrganizationLevelTransferReasons(organizationId, normalizedFilter, false);

            if (plannedResult.resultType === "Ok") {
                setOrganizationPlannedTransferReasons(plannedResult.data);
            } else {
                setError(plannedResult.errors?.[0]);
            }

            if (unplannedResult.resultType === "Ok") {
                setOrganizationUnplannedTransferReasons(unplannedResult.data);
            } else {
                setError(unplannedResult.errors?.[0]);
            }

            // Also set the combined organizationTransferReasons for backward compatibility
            // (merging both planned and unplanned into one response)
            if (plannedResult.resultType === "Ok" && unplannedResult.resultType === "Ok") {
                const combined: TransferReasonsApiResponse = {};
                // Merge planned data
                Object.entries(plannedResult.data || {}).forEach(([appId, reasons]) => {
                    if (!combined[appId]) combined[appId] = {};
                    Object.entries(reasons).forEach(([reason, count]) => {
                        combined[appId][reason] = (combined[appId][reason] || 0) + count;
                    });
                });
                // Merge unplanned data
                Object.entries(unplannedResult.data || {}).forEach(([appId, reasons]) => {
                    if (!combined[appId]) combined[appId] = {};
                    Object.entries(reasons).forEach(([reason, count]) => {
                        combined[appId][reason] = (combined[appId][reason] || 0) + count;
                    });
                });
                setOrganizationTransferReasons(combined);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize transfer reasons filter');
        } finally {
            setLoadingOrganizationTransferReasons(false);
        }
    };

    const loadPlatformTransferReasons = async (organizationIds: string[], filter: EngagementAnalyticsFilter) => {
        setLoadingPlatformTransferReasons(true);
        try {
            const normalizedFilter = normalizeFilterOrThrow(filter);
            const plannedResult = await voicifyApi.getPlatformTransferReasons(organizationIds, normalizedFilter, true);
            const unplannedResult = await voicifyApi.getPlatformTransferReasons(organizationIds, normalizedFilter, false);

            if (plannedResult.resultType === "Ok") {
                setPlatformPlannedTransferReasons(plannedResult.data);
            } else {
                setError(plannedResult.errors?.[0]);
            }

            if (unplannedResult.resultType === "Ok") {
                setPlatformUnplannedTransferReasons(unplannedResult.data);
            } else {
                setError(unplannedResult.errors?.[0]);
            }

            if (plannedResult.resultType === "Ok" && unplannedResult.resultType === "Ok") {
                const combined: TransferReasonsApiResponse = {};
                Object.entries(plannedResult.data || {}).forEach(([organizationId, reasons]) => {
                    if (!combined[organizationId]) combined[organizationId] = {};
                    Object.entries(reasons).forEach(([reason, count]) => {
                        combined[organizationId][reason] = (combined[organizationId][reason] || 0) + count;
                    });
                });
                Object.entries(unplannedResult.data || {}).forEach(([organizationId, reasons]) => {
                    if (!combined[organizationId]) combined[organizationId] = {};
                    Object.entries(reasons).forEach(([reason, count]) => {
                        combined[organizationId][reason] = (combined[organizationId][reason] || 0) + count;
                    });
                });
                setPlatformTransferReasons(combined);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize platform transfer reasons filter');
        } finally {
            setLoadingPlatformTransferReasons(false);
        }
    };

    const loadApplicationTransferReasons = async (applicationId: string, filter: EngagementAnalyticsFilter, getPlanned: boolean = false) => {
        setLoadingApplicationTransferReasons(true);
        const result = await voicifyApi.getApplicationLevelTransferReasons(applicationId, filter, getPlanned);
        setLoadingApplicationTransferReasons(false);
        if (result.resultType === "Ok") {
            setApplicationTransferReasons(result.data);
        } else {
            setError(result.errors?.[0]);
        }
    };

    // #endregion

    // #region Appointments Managed Loaders (for Patient Bookings chart)
    // Note: Filters from organizationEngagements client-side by sessionTags
    // to avoid redundant API calls

    // Helper to filter engagements by sessionCategory and sessionTags
    const filterEngagementsByTags = (
        engagements: voicifyApi.OrganizationEngagement[],
        category: string,
        requiredTags: string[]
    ): voicifyApi.OrganizationEngagement[] => {
        return engagements.filter(e => {
            const matchesCategory = e.sessionCategory === category;
            const tags = e.sessionTags || [];
            const hasRequiredTags = requiredTags.every(tag => tags.includes(tag));
            return matchesCategory && hasRequiredTags;
        });
    };

    // These individual loaders now filter from organizationEngagements
    // They should only be called AFTER loadOrganizationEngagements has completed
    const filterScheduledEngagementsFromLoaded = () => {
        const filtered = filterEngagementsByTags(organizationEngagements, 'ACTION_ATTEMPTED', ['ACTION_COMPLETED']);
        setScheduledEngagements(filtered);
    };

    const filterRescheduledEngagementsFromLoaded = () => {
        const filtered = filterEngagementsByTags(organizationEngagements, 'ACTION_ATTEMPTED', ['APPOINTMENT_RESCHEDULED']);
        setRescheduledEngagements(filtered);
    };

    const filterCancelledEngagementsFromLoaded = () => {
        const filtered = filterEngagementsByTags(organizationEngagements, 'ACTION_ATTEMPTED', ['APPOINTMENT_CANCELLED']);
        setCancelledEngagements(filtered);
    };

    const filterCheckNextAppointmentEngagementsFromLoaded = () => {
        const filtered = filterEngagementsByTags(organizationEngagements, 'ACTION_ATTEMPTED', ['CHECK_NEXT_APPOINTMENT_COMPLETE']);
        setCheckNextAppointmentEngagements(filtered);
    };

    // Individual loaders using getEngagementsForTagCombo API for efficient tag-based queries
    const loadScheduledEngagements = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingScheduledEngagements(true);
        try {
            const normalizedFilter = normalizeFilterOrThrow(filter);
            const result = await voicifyApi.getEngagementsForTagCombo(organizationId, normalizedFilter, '', ['ACTION_COMPLETED'], []);
            if (result.resultType === "Ok") {
                setScheduledEngagements(flattenGroupedEngagements(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize scheduled engagements filter');
        } finally {
            setLoadingScheduledEngagements(false);
        }
    };

    const loadRescheduledEngagements = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingRescheduledEngagements(true);
        try {
            const normalizedFilter = normalizeFilterOrThrow(filter);
            const result = await voicifyApi.getEngagementsForTagCombo(organizationId, normalizedFilter, '', ['APPOINTMENT_RESCHEDULED'], []);
            if (result.resultType === "Ok") {
                setRescheduledEngagements(flattenGroupedEngagements(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize rescheduled engagements filter');
        } finally {
            setLoadingRescheduledEngagements(false);
        }
    };

    const loadCancelledEngagements = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingCancelledEngagements(true);
        try {
            const normalizedFilter = normalizeFilterOrThrow(filter);
            const result = await voicifyApi.getEngagementsForTagCombo(organizationId, normalizedFilter, '', ['APPOINTMENT_CANCELLED'], []);
            if (result.resultType === "Ok") {
                setCancelledEngagements(flattenGroupedEngagements(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize cancelled engagements filter');
        } finally {
            setLoadingCancelledEngagements(false);
        }
    };

    const loadCheckNextAppointmentEngagements = async (organizationId: string, filter: EngagementAnalyticsFilter) => {
        setLoadingCheckNextAppointmentEngagements(true);
        try {
            const normalizedFilter = normalizeFilterOrThrow(filter);
            const result = await voicifyApi.getEngagementsForTagCombo(organizationId, normalizedFilter, '', ['CHECK_NEXT_APPOINTMENT_COMPLETE'], []);
            if (result.resultType === "Ok") {
                setCheckNextAppointmentEngagements(flattenGroupedEngagements(result.data));
            } else {
                setError(result.errors?.[0]);
            }
        } catch (error) {
            setError(error?.message || 'Failed to normalize check-next-appointment filter');
        } finally {
            setLoadingCheckNextAppointmentEngagements(false);
        }
    };

    // Load all scheduling events by fetching organizationEngagements once and filtering
    // This also populates organizationEngagements for other uses
    // Pattern based on SessionAnalyticsContainer.loadSessionsForOrgs
    // @param maxEngagementsCap - Optional cap to limit in-memory accumulation (defaults to MAX_ENGAGEMENTS_CAP)
    const loadAllSchedulingEngagements = async (
        organizationId: string,
        filter: EngagementAnalyticsFilter,
        maxEngagementsCap: number = MAX_ENGAGEMENTS_CAP
    ) => {
        setLoadingScheduledEngagements(true);
        setLoadingRescheduledEngagements(true);
        setLoadingCancelledEngagements(true);
        setLoadingCheckNextAppointmentEngagements(true);
        setLoadingOrganizationEngagements(true);
        setError("");

        // Clear previous data to free memory before loading new data
        setOrganizationEngagements([]);
        setScheduledEngagements([]);
        setRescheduledEngagements([]);
        setCancelledEngagements([]);
        setCheckNextAppointmentEngagements([]);

        let allEngagements: voicifyApi.OrganizationEngagement[] = [];
        let skip = 0;
        const pageSize = filter.take ?? ENGAGEMENT_PAGE_SIZE;
        let hasMoreData = true;
        let reachedCap = false;

        try {
            if (!filter?.startDate || !filter?.endDate) {
                const message = '[loadAllSchedulingEngagements] Missing startDate or endDate in filter.';
                console.error(message, { startDate: filter?.startDate, endDate: filter?.endDate });
                setError(message);
                return { engagements: allEngagements, reachedCap: false, cap: maxEngagementsCap };
            }

            const normalizedFilter = normalizeFilterOrThrow(filter);
            const startMoment = moment(normalizedFilter.startDate);
            const endMoment = moment(normalizedFilter.endDate);
            if (!startMoment.isValid() || !endMoment.isValid()) {
                const message = '[loadAllSchedulingEngagements] Invalid startDate or endDate in filter.';
                console.error(message, { startDate: filter.startDate, endDate: filter.endDate });
                setError(message);
                return { engagements: allEngagements, reachedCap: false, cap: maxEngagementsCap };
            }

            while (hasMoreData) {
                // Check if we've reached the cap before fetching more data
                if (allEngagements.length >= maxEngagementsCap) {
                    console.warn(
                        `[loadAllSchedulingEngagements] Reached maximum engagements cap (${maxEngagementsCap}). ` +
                        `Returning partial results with ${allEngagements.length} engagements. ` +
                        `Consider narrowing the date range or increasing REACT_APP_MAX_ENGAGEMENTS_CAP.`
                    );
                    reachedCap = true;
                    hasMoreData = false;
                    break;
                }

                const paginatedFilter: EngagementAnalyticsFilter = {
                    ...normalizedFilter,
                    skip,
                    take: pageSize
                };

                const result = await voicifyApi.getOrganizationEngagements(organizationId, paginatedFilter);

                if (result.resultType === "Ok") {
                    const pageEngagements = result.data || [];
                    allEngagements.push(...pageEngagements);

                    // Check if adding this page exceeded the cap
                    if (allEngagements.length >= maxEngagementsCap) {
                        console.warn(
                            `[loadAllSchedulingEngagements] Reached maximum engagements cap (${maxEngagementsCap}). ` +
                            `Returning partial results with ${allEngagements.length} engagements. ` +
                            `Consider narrowing the date range or increasing REACT_APP_MAX_ENGAGEMENTS_CAP.`
                        );
                        reachedCap = true;
                        hasMoreData = false;
                    } else if (pageEngagements.length < pageSize || pageEngagements.length === 0) {
                        // If we received fewer records than requested or none, we've reached the end
                        hasMoreData = false;
                    } else {
                        skip += pageSize;
                    }
                } else {
                    setError(result.errors?.[0] || 'Failed to load engagements');
                    hasMoreData = false;
                }
            }

            // Store all engagements (this also serves loadOrganizationEngagements)
            setOrganizationEngagements(allEngagements);

            // Filter for each category
            const scheduled = filterEngagementsByTags(allEngagements, 'ACTION_ATTEMPTED', ['ACTION_COMPLETED']);
            const rescheduled = filterEngagementsByTags(allEngagements, 'ACTION_ATTEMPTED', ['APPOINTMENT_RESCHEDULED']);
            const cancelled = filterEngagementsByTags(allEngagements, 'ACTION_ATTEMPTED', ['APPOINTMENT_CANCELLED']);
            const checkNext = filterEngagementsByTags(allEngagements, 'ACTION_ATTEMPTED', ['CHECK_NEXT_APPOINTMENT_COMPLETE']);

            setScheduledEngagements(scheduled);
            setRescheduledEngagements(rescheduled);
            setCancelledEngagements(cancelled);
            setCheckNextAppointmentEngagements(checkNext);

            // Return info about whether cap was reached (useful for callers)
            return { engagements: allEngagements, reachedCap, cap: maxEngagementsCap };
        } catch (error) {
            console.error('[loadAllSchedulingEngagements] Error:', error);
            setError(error?.message || 'Unknown error loading engagements');
            return { engagements: allEngagements, reachedCap: false, cap: maxEngagementsCap };
        } finally {
            setLoadingScheduledEngagements(false);
            setLoadingRescheduledEngagements(false);
            setLoadingCancelledEngagements(false);
            setLoadingCheckNextAppointmentEngagements(false);
            setLoadingOrganizationEngagements(false);
        }
    };

    // #endregion

    const resetStats = () => {
        setCallStatistics(null);
        setOrdersPlacedCount(0);
        setConversationalSuccessRate(null);
        setTopIntents(null);
        setOrderTotalStatistics(null);
    };

    return {
        getOrgLevelCallStatistics,
        getOrgLevelTopIntents,
        getOrgLevelOrdersPlaced,
        getOrgLevelConversationalSuccess,
        getOrgLevelItemAddedToOrder,
        getOrgLevelOrderTotalStatistics,
        getTopLocationsWithNumberOfOrdersPlaced,
        getSessionsOfAllApplications,
        loadAllEventsForSession,
        clearEventsCache,
        sessions,

        loadOrgAnalytics,
        loadOrgApplicationsPerformanceAnalytics,
        loadingSessionId,
        resetStats,
        allEventsCache,
        orderAttempts,
        callStatistics,
        orderPlacedEvents,
        topIntents,
        conversationalSuccessEvents,
        itemAddedToOrderEvents,
        orderTotalStatistics,
        topLocationsWithNumberOfOrdersPlaced,
        orgApplicationsPerformanceAnalytics,

        orderPlacedRate,
        orderPlacedCount,
        orderTransferRate,
        orderTransfers,
        sessionIdCount,

        conversationalSuccessCount,
        conversationalSuccessWithOrderAttemptedCount,
        conversationalSuccessRate,
        attemptedOrdersConversationalSuccessRate,
        itemAddedConversationalSuccessEvents,
        noTransferUnsuccessfulSessions,
        transferUnsuccessfulSessions,

        transfers,
        immediateTransfers,
        callTransferRate,
        immediateCallTransferRate,

        unsuccessfulOrderSessions,
        ordersPlacedSessions,
        orderTransferredSessionIds,
        noOrderSessionIds,

        error,
        loadingTopIntents,
        loadingOrdersPlaced,
        loadingCallStatistics,
        loadingConversationalSucess,
        loadingItemAddedToOrder,
        loadingOrderTotalStatistics,
        loadingTopLocationWithHighestNumberOfOrders,
        loadingOrgAnalytics,
        loadingOrgApplicationsPerformanceAnalytics,
        loadingCallTransferredEvents,
        loadingImmediateCallTransferredEvents,
        loadingSessionsOfAllApplications,
        noInteractionCount,
        unsuccessfulOrderAttempts,
        unsuccessfulButTransfered,
        conversationalSuccessSessions,
        successfulOrdersNotPlacedSessions,

        // Engagement Analytics Functions
        loadOrganizationEngagements,
        loadPlatformEngagements,
        loadEngagementsForTagCombo,
        loadPlatformEngagementsForTagCombo,
        loadTopLevelMetrics,
        loadOrganizationTopLevelMetrics,
        loadPlatformTopLevelMetrics,
        loadPlatformUpsoldRevenue,
        // Configurable cap for testability - can be passed to loaders or used for documentation
        MAX_ENGAGEMENTS_CAP,
        loadAverageCallVolumeHistogram,
        loadOrganizationAverageCallVolumeHistogram,
        loadOrganizationAverageCallVolumeByHourHistogram,
        loadPlatformAverageCallVolumeHistogram,
        loadPlatformAverageCallVolumeByHourHistogram,
        loadAverageContainableCallVolumeHistogram,
        loadOrganizationAverageContainableCallVolumeHistogram,
        loadAverageContainedCallVolumeHistogram,
        loadOrganizationAverageContainedCallVolumeHistogram,
        loadCallVolumeHistogram,
        loadOrganizationCallVolumeHistogram,
        loadPlatformCallVolumeHistogram,
        loadContainableCallVolumeHistogram,
        loadOrganizationContainableCallVolumeHistogram,
        loadPlatformContainableCallVolumeHistogram,
        loadContainedCallVolumeHistogram,
        loadOrganizationContainedCallVolumeHistogram,
        loadPlatformContainedCallVolumeHistogram,
        loadSpanishCallVolumeHistogram,
        loadOrganizationSpanishCallVolumeHistogram,
        loadPlatformSpanishCallVolumeHistogram,
        loadSpanishContainableCallVolumeHistogram,
        loadOrganizationSpanishContainableCallVolumeHistogram,
        loadPlatformSpanishContainableCallVolumeHistogram,
        loadSpanishContainedCallVolumeHistogram,
        loadOrganizationSpanishContainedCallVolumeHistogram,
        loadPlatformSpanishContainedCallVolumeHistogram,
        loadConcurrentCallVolumeHistogram,
        loadOrganizationConcurrentCallVolumeHistogram,
        loadPlatformConcurrentCallVolumeHistogram,
        loadOrderAttemptedVolumeHistogram,
        loadOrganizationOrderAttemptedVolumeHistogram,
        loadPlatformOrderAttemptedVolumeHistogram,
        loadOrderPlacedVolumeHistogram,
        loadOrganizationOrderPlacedVolumeHistogram,
        loadPlatformOrderPlacedVolumeHistogram,

        // Engagement Analytics State
        organizationEngagements,
        organizationEngagementsPerApp,
        platformEngagements,
        platformEngagementsPerOrg,
        engagementsForTagCombo,
        platformEngagementsForTagCombo,
        topLevelMetrics,
        organizationTopLevelMetrics,
        organizationTopLevelMetricsPerApp,
        platformTopLevelMetrics,
        platformTopLevelMetricsPerOrg,
        platformUpsoldRevenuePerOrg,
        callVolumeHistogram,
        organizationCallVolumeHistogram,
        organizationCallVolumeHistogramPerApp,
        platformCallVolumeHistogram,
        platformCallVolumeHistogramPerOrg,
        averageCallVolumeHistogram,
        organizationAverageCallVolumeHistogram,
        organizationAverageCallVolumeHistogramPerApp,
        organizationAverageCallVolumeByHourHistogram,
        organizationAverageCallVolumeByHourHistogramPerApp,
        platformAverageCallVolumeHistogram,
        platformAverageCallVolumeHistogramPerOrg,
        platformAverageCallVolumeByHourHistogram,
        platformAverageCallVolumeByHourHistogramPerOrg,
        containableCallVolumeHistogram,
        organizationContainableCallVolumeHistogram,
        organizationContainableCallVolumeHistogramPerApp,
        platformContainableCallVolumeHistogram,
        platformContainableCallVolumeHistogramPerOrg,
        containedCallVolumeHistogram,
        organizationContainedCallVolumeHistogram,
        organizationContainedCallVolumeHistogramPerApp,
        platformContainedCallVolumeHistogram,
        platformContainedCallVolumeHistogramPerOrg,
        averageContainableCallVolumeHistogram,
        organizationAverageContainableCallVolumeHistogram,
        averageContainedCallVolumeHistogram,
        organizationAverageContainedCallVolumeHistogram,
        spanishCallVolumeHistogram,
        organizationSpanishCallVolumeHistogram,
        organizationSpanishCallVolumeHistogramPerApp,
        platformSpanishCallVolumeHistogram,
        platformSpanishCallVolumeHistogramPerOrg,
        spanishContainableCallVolumeHistogram,
        organizationSpanishContainableCallVolumeHistogram,
        organizationSpanishContainableCallVolumeHistogramPerApp,
        platformSpanishContainableCallVolumeHistogram,
        platformSpanishContainableCallVolumeHistogramPerOrg,
        spanishContainedCallVolumeHistogram,
        organizationSpanishContainedCallVolumeHistogram,
        organizationSpanishContainedCallVolumeHistogramPerApp,
        platformSpanishContainedCallVolumeHistogram,
        platformSpanishContainedCallVolumeHistogramPerOrg,
        concurrentCallVolumeHistogram,
        organizationConcurrentCallVolumeHistogram,
        organizationConcurrentCallVolumeHistogramPerApp,
        platformConcurrentCallVolumeHistogram,
        platformConcurrentCallVolumeHistogramPerOrg,
        orderAttemptedVolumeHistogram,
        organizationOrderAttemptedVolumeHistogram,
        organizationOrderAttemptedVolumeHistogramPerApp,
        platformOrderAttemptedVolumeHistogram,
        platformOrderAttemptedVolumeHistogramPerOrg,
        orderPlacedVolumeHistogram,
        organizationOrderPlacedVolumeHistogram,
        organizationOrderPlacedVolumeHistogramPerApp,
        platformOrderPlacedVolumeHistogram,
        platformOrderPlacedVolumeHistogramPerOrg,

        // Engagement Analytics Loading State
        loadingOrganizationEngagements,
        loadingPlatformEngagements,
        loadingEngagementsForTagCombo,
        loadingPlatformEngagementsForTagCombo,
        loadingTopLevelMetrics,
        loadingOrganizationTopLevelMetrics,
        loadingPlatformTopLevelMetrics,
        loadingPlatformUpsoldRevenue,
        loadingCallVolumeHistogram,
        loadingOrganizationCallVolumeHistogram,
        loadingPlatformCallVolumeHistogram,
        loadingAverageCallVolumeHistogram,
        loadingOrganizationAverageCallVolumeHistogram,
        loadingOrganizationAverageCallVolumeByHourHistogram,
        loadingPlatformAverageCallVolumeHistogram,
        loadingPlatformAverageCallVolumeByHourHistogram,
        loadingContainableCallVolumeHistogram,
        loadingOrganizationContainableCallVolumeHistogram,
        loadingPlatformContainableCallVolumeHistogram,
        loadingContainedCallVolumeHistogram,
        loadingOrganizationContainedCallVolumeHistogram,
        loadingPlatformContainedCallVolumeHistogram,
        loadingAverageContainableCallVolumeHistogram,
        loadingOrganizationAverageContainableCallVolumeHistogram,
        loadingAverageContainedCallVolumeHistogram,
        loadingOrganizationAverageContainedCallVolumeHistogram,
        loadingSpanishCallVolumeHistogram,
        loadingOrganizationSpanishCallVolumeHistogram,
        loadingPlatformSpanishCallVolumeHistogram,
        loadingSpanishContainableCallVolumeHistogram,
        loadingOrganizationSpanishContainableCallVolumeHistogram,
        loadingPlatformSpanishContainableCallVolumeHistogram,
        loadingSpanishContainedCallVolumeHistogram,
        loadingOrganizationSpanishContainedCallVolumeHistogram,
        loadingPlatformSpanishContainedCallVolumeHistogram,
        loadingConcurrentCallVolumeHistogram,
        loadingOrganizationConcurrentCallVolumeHistogram,
        loadingPlatformConcurrentCallVolumeHistogram,
        loadingOrderAttemptedVolumeHistogram,
        loadingOrganizationOrderAttemptedVolumeHistogram,
        loadingPlatformOrderAttemptedVolumeHistogram,
        loadingOrderPlacedVolumeHistogram,
        loadingOrganizationOrderPlacedVolumeHistogram,
        loadingPlatformOrderPlacedVolumeHistogram,

        // Transfer Reasons
        loadOrganizationTransferReasons,
        loadPlatformTransferReasons,
        loadApplicationTransferReasons,
        organizationTransferReasons,
        organizationPlannedTransferReasons,
        organizationUnplannedTransferReasons,
        platformTransferReasons,
        platformPlannedTransferReasons,
        platformUnplannedTransferReasons,
        applicationTransferReasons,
        loadingOrganizationTransferReasons,
        loadingPlatformTransferReasons,
        loadingApplicationTransferReasons,

        // Appointments Managed (for Patient Bookings)
        loadScheduledEngagements,
        loadRescheduledEngagements,
        loadCancelledEngagements,
        loadCheckNextAppointmentEngagements,
        loadAllSchedulingEngagements,
        scheduledEngagements,
        rescheduledEngagements,
        cancelledEngagements,
        checkNextAppointmentEngagements,
        loadingScheduledEngagements,
        loadingRescheduledEngagements,
        loadingCancelledEngagements,
        loadingCheckNextAppointmentEngagements,

        // Off Hours Engagements
        loadOffHoursEngagements,
        offHoursEngagements,
        loadingOffHoursEngagements,

        // Spanish Off Hours Engagements
        loadSpanishOffHoursEngagements,
        spanishOffHoursEngagements,
        loadingSpanishOffHoursEngagements,

        // Transferred Engagements
        loadTransferredEngagements,
        transferredEngagements,
        loadingTransferredEngagements
    };

}
const OrgAnalyticsContainer = createContainer(useOrgAnalyticsContainer);
export default OrgAnalyticsContainer;
